


import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Tabs, Tab, Chip, TextField, InputAdornment, IconButton, ButtonGroup,
  FormLabel,
  RadioGroup,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio, Collapse, Divider
} from '@mui/material';
import {
  GridView as GridViewIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  Schedule as ScheduleIcon,
  Group as GroupIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon, KeyboardArrowDown, KeyboardArrowRight, Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import axios from 'axios';
import { useTheme, alpha } from '@mui/material/styles';
import { ArchiveRestoreIcon } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const UPS_OPTIONS = Array.from({ length: 20 }, (_, index) => index + 1);
const AUDIT_TIMESTAMP_ADJUSTMENT_MINUTES = 5 * 60 + 30;

const timeToSeconds = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const parts = String(value).split(':').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return 0;
  return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
};

const secondsBetween = (start, end) => {
  if (!start) return 0;
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return 0;
  return Math.floor((endMs - startMs) / 1000);
};

const rowDurationSeconds = (row) => {
  const durationSeconds = timeToSeconds(row.duration);
  if (durationSeconds > 0) return durationSeconds;

  const durationHours = Number(row.durationHours);
  if (Number.isFinite(durationHours) && durationHours > 0) {
    return Math.round(durationHours * 3600);
  }

  return secondsBetween(row.startTimeRaw, row.endTimeRaw);
};

const formatDurationSeconds = (value) => {
  const seconds = Math.max(0, Math.round(Number(value) || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const calculateEmployeeTimeMetrics = (logs) => {
  const jobRows = logs.filter((log) => log.operationDescription === 'JOB_STARTED');

  const totalSeconds = jobRows.reduce(
    (sum, row) => sum + rowDurationSeconds(row),
    0
  );

  let productionSeconds = jobRows.reduce(
    (sum, row) => sum + timeToSeconds(row.prodTime),
    0
  );
  let idleSeconds = jobRows.reduce(
    (sum, row) => sum + timeToSeconds(row.idleTime),
    0
  );
  let mealSeconds = jobRows.reduce(
    (sum, row) => sum + timeToSeconds(row.mealTime),
    0
  );

  // Aggregate columns are updated only when an operation ends. Include any
  // currently running child operation so ongoing commanded time is not
  // incorrectly reported as non-commanded time.
  logs
    .filter((row) => row.operationDescription !== 'JOB_STARTED' && !row.endTimeRaw)
    .forEach((row) => {
      const ongoingSeconds = secondsBetween(row.startTimeRaw, null);
      const code = String(row.operationCode || '').toUpperCase();
      const type = String(row.operationType || '').toLowerCase();

      if (code.startsWith('PROD') || type === 'production') {
        productionSeconds += ongoingSeconds;
      } else if (['IT-TT', 'IT-LT', 'IT-DT'].includes(code) || type === 'meals') {
        mealSeconds += ongoingSeconds;
      } else {
        idleSeconds += ongoingSeconds;
      }
    });

  const commandedSeconds = productionSeconds + idleSeconds + mealSeconds;
  const nonCommandedSeconds = Math.max(totalSeconds - commandedSeconds, 0);
  const commandedRate = totalSeconds > 0
    ? Math.min(100, (commandedSeconds / totalSeconds) * 100)
    : 0;

  return {
    sessions: jobRows.length,
    total: formatDurationSeconds(totalSeconds),
    production: formatDurationSeconds(productionSeconds),
    idle: formatDurationSeconds(idleSeconds),
    meal: formatDurationSeconds(mealSeconds),
    nonCommanded: formatDurationSeconds(nonCommandedSeconds),
    commandedRate: commandedRate.toFixed(1),
  };
};

const getActivityCategory = (row) => {
  if (row.operationDescription === 'JOB_STARTED') return 'session';

  const code = String(row.operationCode || '').trim().toUpperCase();
  const type = String(row.operationType || '').trim().toLowerCase();
  const description = String(row.operationDescription || '').trim().toLowerCase();

  if (code.startsWith('PROD') || type === 'production') return 'production';
  if (
    ['IT-TT', 'IT-LT', 'IT-DT'].includes(code) ||
    type === 'meals' ||
    description.includes('meal') ||
    description.includes('tea') ||
    description.includes('lunch')
  ) return 'meal';
  if (
    type === 'idle' ||
    type === 'setup' ||
    code.startsWith('IT-') ||
    description.includes('idle') ||
    description.includes('setup')
  ) return 'idle';

  return 'other';
};

const calculateNonCommandedGaps = (logs, windowStart, windowEnd) => {
  const startMs = new Date(windowStart).getTime();
  const rawEndMs = windowEnd ? new Date(windowEnd).getTime() : Date.now();
  const endMs = Math.min(rawEndMs, Date.now());

  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return [];
  }

  const intervals = logs
    .filter((row) => getActivityCategory(row) !== 'session')
    .map((row) => {
      const activityStart = new Date(row.startTimeRaw).getTime();
      const activityEnd = row.endTimeRaw
        ? new Date(row.endTimeRaw).getTime()
        : Date.now();

      return {
        start: Math.max(startMs, activityStart),
        end: Math.min(endMs, activityEnd),
      };
    })
    .filter(
      (interval) =>
        Number.isFinite(interval.start) &&
        Number.isFinite(interval.end) &&
        interval.end > interval.start
    )
    .sort((a, b) => a.start - b.start);

  const merged = [];
  intervals.forEach((interval) => {
    const previous = merged[merged.length - 1];
    if (!previous || interval.start > previous.end) {
      merged.push({ ...interval });
    } else {
      previous.end = Math.max(previous.end, interval.end);
    }
  });

  const gaps = [];
  let cursor = startMs;

  merged.forEach((interval) => {
    if (interval.start > cursor) {
      gaps.push({
        start: new Date(cursor),
        end: new Date(interval.start),
        durationSeconds: Math.floor((interval.start - cursor) / 1000),
      });
    }
    cursor = Math.max(cursor, interval.end);
  });

  if (cursor < endMs) {
    gaps.push({
      start: new Date(cursor),
      end: new Date(endMs),
      durationSeconds: Math.floor((endMs - cursor) / 1000),
    });
  }

  return gaps.filter((gap) => gap.durationSeconds > 0);
};

const buildTimelineRows = (
  logs,
  gaps = [],
  windowStart = null,
  windowEnd = null
) => {
  const activityRows = logs.map((row) => {
    if (!windowStart || getActivityCategory(row) === 'session') {
      return {
        ...row,
        activityCategory: getActivityCategory(row),
        isNonCommandedGap: false,
      };
    }

    const windowStartMs = new Date(windowStart).getTime();
    const windowEndMs = windowEnd ? new Date(windowEnd).getTime() : Date.now();
    const rowStartMs = new Date(row.startTimeRaw).getTime();
    const rowEndMs = row.endTimeRaw
      ? new Date(row.endTimeRaw).getTime()
      : Date.now();
    const clippedStartMs = Math.max(windowStartMs, rowStartMs);
    const clippedEndMs = Math.min(windowEndMs, rowEndMs);
    const clippedSeconds = Math.max(
      0,
      Math.floor((clippedEndMs - clippedStartMs) / 1000)
    );

    return {
      ...row,
      activityCategory: getActivityCategory(row),
      startTime: dayjs(clippedStartMs).format('YYYY-MM-DD HH:mm:ss'),
      startTimeRaw: new Date(clippedStartMs),
      endTime: dayjs(clippedEndMs).format('YYYY-MM-DD HH:mm:ss'),
      endTimeRaw: new Date(clippedEndMs),
      durationMinutes: Math.round(clippedSeconds / 60),
      durationHours: Number((clippedSeconds / 3600).toFixed(2)),
      duration: new Date(clippedSeconds * 1000).toISOString().slice(11, 19),
      isSessionClipped: clippedStartMs !== rowStartMs || clippedEndMs !== rowEndMs,
      isNonCommandedGap: false,
    };
  });
  const gapRows = gaps.map((gap, index) => ({
    id: `non-commanded-${gap.start.getTime()}-${gap.end.getTime()}-${index}`,
    operationDescription: '',
    activityCategory: 'non-commanded',
    startTime: dayjs(gap.start).format('YYYY-MM-DD HH:mm:ss'),
    startTimeRaw: gap.start,
    endTime: dayjs(gap.end).format('YYYY-MM-DD HH:mm:ss'),
    endTimeRaw: gap.end,
    duration: new Date(gap.durationSeconds * 1000).toISOString().slice(11, 19),
    isApproved: null,
    isNonCommandedGap: true,
  }));

  return [...activityRows, ...gapRows].sort((first, second) => {
    const firstTime = new Date(first.startTimeRaw || first.startTime).getTime();
    const secondTime = new Date(second.startTimeRaw || second.startTime).getTime();
    return firstTime - secondTime;
  });
}

const getActivityRowClass = (row, highlightedRows = []) => {
  if (highlightedRows.includes(row.id)) return 'row-highlight';
  if (row.isNonCommandedGap || row.activityCategory === 'non-commanded') {
    return 'activity-row-non-commanded';
  }

  const category = row.activityCategory || getActivityCategory(row);
  return `activity-row-${category}`;
};

const getLogsOverlappingWindow = (logs, windowStart, windowEnd) => {
  const startMs = new Date(windowStart).getTime();
  const endMs = windowEnd ? new Date(windowEnd).getTime() : Date.now();

  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return [];
  }

  return logs.filter((row) => {
    const rowStartMs = new Date(row.startTimeRaw).getTime();
    const rowEndMs = row.endTimeRaw
      ? new Date(row.endTimeRaw).getTime()
      : Date.now();

    return Number.isFinite(rowStartMs) &&
      Number.isFinite(rowEndMs) &&
      rowStartMs < endMs &&
      rowEndMs > startMs;
  });
};

const getClippedRowDurationSeconds = (row, windowStart, windowEnd) => {
  const windowStartMs = new Date(windowStart).getTime();
  const windowEndMs = windowEnd ? new Date(windowEnd).getTime() : Date.now();
  const rowStartMs = new Date(row.startTimeRaw).getTime();
  const rowEndMs = row.endTimeRaw
    ? new Date(row.endTimeRaw).getTime()
    : Date.now();

  if (
    !Number.isFinite(windowStartMs) ||
    !Number.isFinite(windowEndMs) ||
    !Number.isFinite(rowStartMs) ||
    !Number.isFinite(rowEndMs)
  ) {
    return 0;
  }

  const clippedStart = Math.max(windowStartMs, rowStartMs);
  const clippedEnd = Math.min(windowEndMs, rowEndMs);
  return clippedEnd > clippedStart
    ? Math.floor((clippedEnd - clippedStart) / 1000)
    : 0;
};

const calculateCategorizedTimeMetrics = (
  logs,
  totalWindowSeconds = 0,
  windowStart = null,
  windowEnd = null
) => {
  const totals = {
    production: 0,
    idle: 0,
    meal: 0,
    other: 0,
  };

  logs
    .filter((row) => getActivityCategory(row) !== 'session')
    .forEach((row) => {
      const category = getActivityCategory(row);
      totals[category] += windowStart
        ? getClippedRowDurationSeconds(row, windowStart, windowEnd)
        : rowDurationSeconds(row);
    });

  const commandedSeconds =
    totals.production + totals.idle + totals.meal + totals.other;
  const totalSeconds = Math.max(totalWindowSeconds, commandedSeconds);
  const nonCommandedSeconds = Math.max(totalSeconds - commandedSeconds, 0);
  const commandedRate = totalSeconds > 0
    ? Math.min(100, (commandedSeconds / totalSeconds) * 100)
    : 0;

  return {
    totalSeconds,
    commandedSeconds,
    nonCommandedSeconds,
    productionSeconds: totals.production,
    idleSeconds: totals.idle,
    mealSeconds: totals.meal,
    otherSeconds: totals.other,
    total: formatDurationSeconds(totalSeconds),
    production: formatDurationSeconds(totals.production),
    idle: formatDurationSeconds(totals.idle),
    meal: formatDurationSeconds(totals.meal),
    other: formatDurationSeconds(totals.other),
    nonCommanded: formatDurationSeconds(nonCommandedSeconds),
    commandedRate: commandedRate.toFixed(1),
  };
};

const OperationsButton = ({
  machineCode,
  groupCode,
  supervisorId,
  onTriggerSnackbar = () => { },
  disabled = false,
  sx = {},
}) => {
  const theme = useTheme();
  const isBreakingGroup = String(groupCode || '').trim().toUpperCase() === 'BRT';

  // Safe comparison helper
  // Compare strings safely
  const normalize = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'number' && isNaN(val)) return '';
    return val.toString().trim();
  };

  // Compare numeric fields safely (treat '', null, undefined as 0 for equality)
  const eqNum = (a, b) => Number(a ?? 0) === Number(b ?? 0);


  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [detailActiveTab, setDetailActiveTab] = useState(0);
  const [employeeAnalysisTab, setEmployeeAnalysisTab] = useState(0);
  const [employeeViewMode, setEmployeeViewMode] = useState('period');
  const [selectedTimeRange, setSelectedTimeRange] = useState('today');
  const [searchTerm, setSearchTerm] = useState('');
  const [jobStatusFilter, setJobStatusFilter] = useState('all');
  const [manShiftLogs, setManShiftLogs] = useState([]);
  const [manDetailLogs, setManDetailLogs] = useState([]);
  const [highlightedRows, setHighlightedRows] = useState([]);


  const [expandedSubDetails, setExpandedSubDetails] = useState({});

  // Track which job card is expanded (accordion behaviour)
  const [expandedGroup, setExpandedGroup] = useState(null);

  // Track employee collapses per job
  const [expandedSubGroups, setExpandedSubGroups] = useState({});

  // Add these state variables around line 45-50 with other states
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [pendingApprovalLogs, setPendingApprovalLogs] = useState([]);


  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [formValues, setFormValues] = useState({
    job_number: '',
    customer: '',
    product: '',
    main_operation: '',
    up_count: '',
    prod_qty: '',
    waste_qty: '',
  });

  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);

  const handleToggleGroup = useCallback((groupKey) => {
    setExpandedGroup((prev) => (prev === groupKey ? null : groupKey));
  }, []);



  const triggerSnackbar = useCallback(
    (message, severity) => {
      if (typeof onTriggerSnackbar === 'function') onTriggerSnackbar(message, severity);
    },
    [onTriggerSnackbar]
  );

  const handleOpenEditDialog = useCallback((row) => {
    if (!row?.jobStartedTimeRaw) {
      triggerSnackbar('Cannot edit: no Job Started Time', 'info');
      return;
    }

    setEditRow(row);
    setFormValues({
      job_number: row.jobNumber || '',
      customer: row.customer || '',
      product: row.product || '',
      main_operation: row.mainOperation || '',
      up_count: row.upCount ?? '',
      remarks: row.remarks || '',
      finished_good_status: row.finishedGoodStatus || '',
      job_status: row.jobStatus || '',
      prod_qty: row.prodQty !== undefined && row.prodQty !== null ? String(row.prodQty) : '',
      waste_qty: row.wasteQty !== undefined && row.wasteQty !== null ? String(row.wasteQty) : '',
    });

    setEditDialogOpen(true);
  }, [triggerSnackbar]);


  const timeRanges = useMemo(() => {
    const now = dayjs();
    return {
      today: { label: 'Today', start: now.startOf('day'), end: now.endOf('day'), icon: <TodayIcon fontSize="small" /> },
      yesterday: { label: 'Yesterday', start: now.subtract(1, 'day').startOf('day'), end: now.subtract(1, 'day').endOf('day'), icon: <CalendarTodayIcon fontSize="small" /> },
      last24Hours: { label: 'Last 24 Hours', start: now.subtract(24, 'hours'), end: now, icon: <AccessTimeIcon fontSize="small" /> },
      last48Hours: { label: 'Last 48 Hours', start: now.subtract(48, 'hours'), end: now, icon: <AccessTimeIcon fontSize="small" /> },
      thisWeek: { label: 'This Week', start: now.startOf('week'), end: now.endOf('week'), icon: <DateRangeIcon fontSize="small" /> },
      lastWeek: { label: 'Last Week', start: now.subtract(1, 'week').startOf('week'), end: now.subtract(1, 'week').endOf('week'), icon: <ScheduleIcon fontSize="small" /> },
      last7Days: { label: 'Last 7 Days', start: now.subtract(7, 'days'), end: now, icon: <ScheduleIcon fontSize="small" /> },
      thisMonth: { label: 'This Month', start: now.startOf('month'), end: now.endOf('month'), icon: <CalendarTodayIcon fontSize="small" /> },
    };
  }, []);


  const shiftColumns = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 70 },
      { field: 'supervisor', headerName: 'Supervisor', width: 130 },
      { field: 'employee', headerName: 'Employee', width: 130 },
      { field: 'stationCode', headerName: 'Station', width: 90 },
      { field: 'shiftIn', headerName: 'Shift In', width: 140 },
      { field: 'shiftOut', headerName: 'Shift Out', width: 140 },
      { field: 'remarks', headerName: 'Shift Remarks', width: 220 },
    ],
    []
  );

  const detailColumns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        width: 70,
        renderCell: (params) => params.row.isNonCommandedGap ? '—' : params.value,
      },
      { field: 'supervisor', headerName: 'Supervisor', width: 130 },
      { field: 'employee', headerName: 'Employee', width: 130 },
      { field: 'stationCode', headerName: 'Station', width: 90 },
      { field: 'jobNumber', headerName: 'Job #', width: 100 },
      { field: 'customer', headerName: 'Customer', width: 150 },
      { field: 'product', headerName: 'Product', width: 150 },
      { field: 'mainOperation', headerName: 'Operation', width: 150 },
      { field: 'operationCode', headerName: 'Op Code', width: 100 },
      { field: 'operationDescription', headerName: 'Description', width: 160 },
      {
        field: 'activityCategory',
        headerName: 'Category',
        width: 145,
        valueGetter: (value, row) =>
          row.activityCategory || getActivityCategory(row),
        renderCell: (params) => {
          const category = params.row.activityCategory || getActivityCategory(params.row);
          const categoryConfig = {
            production: { label: 'Production', color: 'success' },
            idle: { label: 'Idle / Setup', color: 'warning' },
            meal: { label: 'Meal', color: 'info' },
            other: { label: 'Other Activity', color: 'secondary' },
            session: { label: 'Job Session', color: 'primary' },
            'non-commanded': { label: 'Non-commanded', color: 'error' },
          };
          const config = categoryConfig[category] || categoryConfig.other;

          return (
            <Chip
              size="small"
              color={config.color}
              variant={category === 'non-commanded' ? 'filled' : 'outlined'}
              label={config.label}
              sx={{ fontWeight: 700 }}
            />
          );
        },
      },
      { field: 'startTime', headerName: 'Start', width: 140 },
      { field: 'endTime', headerName: 'End', width: 140 },
      { field: 'jobStartedTime', headerName: 'Job Start', width: 180 },
      { field: 'upCount', headerName: 'Ups Count', width: 100 },
      { field: 'prodQty', headerName: 'Prod Qty', width: 100 },
      { field: 'wasteQty', headerName: 'Waste Qty', width: 100 },
      { field: 'durationMinutes', headerName: 'Duration (Min)', width: 120 },
      { field: 'durationHours', headerName: 'Duration (Hrs)', width: 120 },
      { field: 'duration', headerName: 'Duration', width: 110 },
      { field: 'operationType', headerName: 'Type', width: 100 },
      { field: 'remarks', headerName: 'Remarks', width: 220 },
      { field: 'prodTime', headerName: 'Production Time', width: 140 },
      { field: 'idleTime', headerName: 'Idle/Setup Time', width: 140 },
      { field: 'mealTime', headerName: 'Meal Time', width: 120 },
      { field: 'finishedGoodStatus', headerName: 'FG Status', width: 120 },
      { field: 'jobStatus', headerName: 'Job Status', width: 120 },
      { field: 'createdAt', headerName: 'Created At', width: 180 },
      { field: 'updatedAt', headerName: 'Updated At', width: 180 },

      // ✅ UPDATED: Show approval status for ALL rows
      {
        field: 'isApproved',
        headerName: 'Approved',
        width: 130,
        renderCell: (params) => {
          const row = params.row;

          if (row.isNonCommandedGap) return null;

          return row.isApproved === 1 ? (
            <Chip
              label={`Yes (${dayjs(row.approvedAt).format('MM-DD HH:mm')})`}
              color="success"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          ) : (
            <Chip
              label="No"
              color="default"
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.grey[300], 0.5),
                fontWeight: 500
              }}
            />
          );
        },
      },

      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const row = params.row;

          // Non-commanded gaps and non-job activities are review-only.
          if (
            row.isNonCommandedGap ||
            !String(row.jobNumber || '').trim()
          ) {
            return null;
          }

          // Job-numbered summary rows retain the existing edit workflow.
          const isEditableRow =
            !row.jobStartedTimeRaw ||
            (row.jobStartedTimeRaw && row.operationDescription === 'JOB_STARTED');

          if (!isEditableRow) return null;

          return (
            <Button
              variant="outlined"
              size="small"
              disabled={row.isApproved === 1 || !row.endTime}
              onClick={() => handleOpenEditDialog(row)}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                opacity: row.isApproved === 1 || !row.endTime ? 0.4 : 1,
              }}
            >
              Edit Job
            </Button>
          );
        },
      },

      {
        field: 'approvedAt',
        headerName: 'Approved At',
        width: 180,
        valueFormatter: (params) =>
          params?.value ? dayjs(params.value).format('YYYY-MM-DD HH:mm:ss') : '-',
      },
    ],
    [handleOpenEditDialog, theme]
  );

  const nonJobDetailColumns = useMemo(
    () => detailColumns.filter((column) => column.field !== 'actions'),
    [detailColumns]
  );

  const groupColumn = useMemo(() => ({
    field: '__groupHeader',
    headerName: '',
    width: 280,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const row = params.row;
      if (!row.__groupStart) return null;

      const groupKey = row.jobStartedTimeRaw;
      const expanded = expandedGroup === groupKey;

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleGroup(groupKey);
            }}
            sx={{
              ml: -1,
              transition: '0.2s ease',
              color: expanded ? 'primary.main' : 'text.secondary',
            }}
          >
            {expanded ? (
              <KeyboardArrowDown fontSize="small" />
            ) : (
              <KeyboardArrowRight fontSize="small" />
            )}
          </IconButton>

          {/* Label Box */}
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 700,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: (theme) => theme.palette.action.hover,
              whiteSpace: 'nowrap',
              minWidth: 120,
              transition: '0.2s ease',
            }}
          >
            {row.__groupLabel}
          </Box>

          <Chip
            size="small"
            label={row.__groupSize}
            sx={{
              fontWeight: 600,
              bgcolor: expanded
                ? alpha(theme.palette.primary.light, 0.3)
                : alpha(theme.palette.grey[300], 0.3),
            }}
          />
        </Box>
      );
    },
  }), [expandedGroup, handleToggleGroup, theme]);



  const handleResetForm = () => {
    if (!editRow) return;
    setFormValues({
      job_number: editRow.jobNumber || '',
      customer: editRow.customer || '',
      product: editRow.product || '',
      main_operation: editRow.mainOperation || '',
      up_count: editRow.upCount ?? '',
      remarks: editRow.remarks || '',
      finished_good_status: editRow.finishedGoodStatus || '',
      job_status: editRow.jobStatus || '',
      prod_qty: editRow.prodQty !== undefined && editRow.prodQty !== null ? String(editRow.prodQty) : '',
      waste_qty: editRow.wasteQty !== undefined && editRow.wasteQty !== null ? String(editRow.wasteQty) : '',
    });
  };


  const handleSaveEdit = () => {
    if (
      isBreakingGroup &&
      editRow?.operationDescription === 'JOB_STARTED' &&
      (!Number.isInteger(Number(formValues.up_count)) ||
        Number(formValues.up_count) < 1 ||
        Number(formValues.up_count) > 20)
    ) {
      triggerSnackbar('Please select the Number of Ups (1–20).', 'warning');
      return;
    }
    setConfirmOpen(true);
  };


  const handleConfirmSave = async () => {
    if (!editRow) return;

    // Identify all rows sharing the same job group
    const relatedIds = manDetailLogs
      .filter(
        (log) =>
          log.supervisor === editRow.supervisor &&
          log.employee === editRow.employee &&
          log.stationCode === editRow.stationCode &&
          log.jobStartedTimeRaw === editRow.jobStartedTimeRaw
      )
      .map((log) => log.id);

    // Build payloads
    const jobWidePayload = { ids: relatedIds };
    const qtyPayload = { id: editRow.id };

    // --- Job-wide editable fields ---
    if (formValues.job_number !== editRow.jobNumber)
      jobWidePayload.job_number = formValues.job_number;
    if (formValues.customer !== editRow.customer)
      jobWidePayload.customer = formValues.customer;
    if (formValues.product !== editRow.product)
      jobWidePayload.product = formValues.product;
    if (formValues.main_operation !== editRow.mainOperation)
      jobWidePayload.main_operation = formValues.main_operation;
    if (formValues.remarks !== editRow.remarks)
      jobWidePayload.remarks = formValues.remarks;

    // --- JOB_STARTED only: prod/waste/FG/JobStatus ---
    if (editRow.operationDescription === 'JOB_STARTED') {
      if (isBreakingGroup) {
        const newUpCount = Number(formValues.up_count);
        const oldUpCount = Number(editRow.upCount ?? 0);
        if (newUpCount !== oldUpCount) qtyPayload.up_count = newUpCount;
      }

      const newProd = formValues.prod_qty === '' ? null : Number(formValues.prod_qty);
      const oldProd = Number(editRow.prodQty ?? 0);
      const newWaste = formValues.waste_qty === '' ? null : Number(formValues.waste_qty);
      const oldWaste = Number(editRow.wasteQty ?? 0);

      if (newProd !== null && newProd !== oldProd) qtyPayload.prod_qty = newProd;
      if (newWaste !== null && newWaste !== oldWaste) qtyPayload.waste_qty = newWaste;

      // FG / Job status (unchanged)
      if (formValues.finished_good_status !== editRow.finishedGoodStatus)
        qtyPayload.finished_good_status = formValues.finished_good_status;
      if (formValues.job_status !== editRow.jobStatus)
        qtyPayload.job_status = formValues.job_status;
    }


    try {
      console.log(" [handleConfirmSave] Payloads built:", {
        jobWidePayload,
        qtyPayload,
      });

      // --- Send updates ---
      if (Object.keys(jobWidePayload).length > 1) {
        console.log(" Sending -> /manhour-update-job-details");
        await axios.post(`${API_BASE_URL}/manhour-update-job-details`, jobWidePayload);
      }

      if (Object.keys(qtyPayload).length > 1) {
        console.log(" Sending -> /manhour-update-quantities");
        await axios.post(`${API_BASE_URL}/manhour-update-quantities`, qtyPayload);
      }

      // --- Update frontend state immediately ---
      setManDetailLogs((prevLogs) =>
        prevLogs.map((log) => {
          //  Multi-record (jobWidePayload)
          if (relatedIds.includes(log.id)) {
            const updatedLog = {
              ...log,
              jobNumber: formValues.job_number ?? log.jobNumber,
              customer: formValues.customer ?? log.customer,
              product: formValues.product ?? log.product,
              mainOperation: formValues.main_operation ?? log.mainOperation,
              remarks: formValues.remarks ?? log.remarks,
            };

            // 2Single-record (qtyPayload)
            if (log.id === editRow.id && log.operationDescription === 'JOB_STARTED') {
              updatedLog.upCount =
                formValues.up_count !== undefined ? formValues.up_count : log.upCount;
              updatedLog.prodQty =
                formValues.prod_qty !== undefined ? formValues.prod_qty : log.prodQty;
              updatedLog.wasteQty =
                formValues.waste_qty !== undefined ? formValues.waste_qty : log.wasteQty;
              updatedLog.finishedGoodStatus =
                formValues.finished_good_status !== undefined
                  ? formValues.finished_good_status
                  : log.finishedGoodStatus;
              updatedLog.jobStatus =
                formValues.job_status !== undefined
                  ? formValues.job_status
                  : log.jobStatus;
            }

            return updatedLog;
          }

          return log;
        })
      );

      //  Force DataGrid re-render for immediate UI update
      setManDetailLogs((prevLogs) => [...prevLogs]);

      // --- UI Feedback ---
      triggerSnackbar(' Job details updated successfully', 'success');
      setConfirmOpen(false);
      setEditDialogOpen(false);

      // --- Highlight logic ---
      const highlightIds =
        Object.keys(qtyPayload).length > 1
          ? [editRow.id] // single record (FG/job status/qty)
          : relatedIds;  // multi-record (job details)

      setHighlightedRows(highlightIds);
      setTimeout(() => setHighlightedRows([]), 4000);

    } catch (err) {
      console.error('Error updating job details:', err);
      triggerSnackbar('Failed to update job details', 'error');
      setConfirmOpen(false);
    }
  };


  const isFieldChanged = (original, updated) => normalize(original) !== normalize(updated);


  const isFormChanged =
    !!editRow &&
    (
      normalize(formValues.job_number) !== normalize(editRow.jobNumber) ||
      normalize(formValues.customer) !== normalize(editRow.customer) ||
      normalize(formValues.product) !== normalize(editRow.product) ||
      normalize(formValues.main_operation) !== normalize(editRow.mainOperation) ||
      normalize(formValues.remarks) !== normalize(editRow.remarks) ||
      normalize(formValues.finished_good_status) !== normalize(editRow.finishedGoodStatus) ||
      normalize(formValues.job_status) !== normalize(editRow.jobStatus) ||
      (
        editRow.operationDescription === 'JOB_STARTED' &&
        (
          (isBreakingGroup && !eqNum(formValues.up_count, editRow.upCount)) ||
          !eqNum(formValues.prod_qty, editRow.prodQty) ||
          !eqNum(formValues.waste_qty, editRow.wasteQty)
        )
      )
    );

  const getCurrentTimeRange = useCallback(() => timeRanges[selectedTimeRange], [selectedTimeRange, timeRanges]);

  const fetchManShiftLog = useCallback(async () => {
    try {
      const timeRange = getCurrentTimeRange();
      const response = await axios.post(`${API_BASE_URL}/ViewManShiftLogForAnalysis`, {
        startTime: timeRange.start.format('YYYY-MM-DD HH:mm:ss'),
        endTime: timeRange.end.format('YYYY-MM-DD HH:mm:ss'),
        machineCode,
      });
      setManShiftLogs(
        response.data.map((shift) => ({
          id: shift.id,
          supervisorId: shift.supervisor_id,
          empId: shift.emp_id,
          supervisor: `${shift.supervisor_name} (${shift.supervisor_id})`,
          employee: `${shift.emp_name} (${shift.emp_id})`,
          stationCode: shift.station_code,
          shiftIn: dayjs(shift.shift_in_datetime).format('YYYY-MM-DD HH:mm'),
          shiftInRaw: shift.shift_in_datetime,
          shiftOut: shift.shift_out_datetime ? dayjs(shift.shift_out_datetime).format('YYYY-MM-DD HH:mm') : null,
          shiftOutRaw: shift.shift_out_datetime,
          remarks: shift.remarks,
        }))
      );
    } catch {
      triggerSnackbar('Error fetching shift logs', 'error');
    }
  }, [machineCode, getCurrentTimeRange, triggerSnackbar]);

  const fetchManDetailLog = useCallback(async () => {
    try {
      const timeRange = getCurrentTimeRange();
      const response = await axios.post(`${API_BASE_URL}/ViewManDetailLogForAnalysis`, {
        startTime: timeRange.start.format('YYYY-MM-DD HH:mm:ss'),
        endTime: timeRange.end.format('YYYY-MM-DD HH:mm:ss'),
        machineCode,
      });
      setManDetailLogs(
        response.data.map((log) => ({
          id: log.id,
          supervisorId: log.supervisor_id,
          empId: log.emp_id,
          supervisor: `${log.supervisor_name} (${log.supervisor_id})`,
          employee: `${log.emp_name} (${log.emp_id})`,
          stationCode: log.station_code,
          jobNumber: log.job_number,
          customer: log.customer,
          product: log.product,
          mainOperation: log.main_operation,
          upCount: log.up_count,
          operationCode: log.operation_code,
          operationDescription: log.operation_description,
          startTime: dayjs(log.start_datetime).format('YYYY-MM-DD HH:mm:ss'),
          startTimeRaw: log.start_datetime,
          endTime: log.end_datetime ? dayjs(log.end_datetime).format('YYYY-MM-DD HH:mm:ss') : null,
          endTimeRaw: log.end_datetime,
          jobStartedTime: log.job_started_time ? dayjs(log.job_started_time).format('YYYY-MM-DD HH:mm:ss.SSS') : null,
          jobStartedTimeRaw: log.job_started_time,
          prodQty: log.prod_qty,
          wasteQty: log.waste_qty,
          durationMinutes: log.duration_minutes,
          durationHours: log.duration_hours,
          duration: log.duration,
          operationType: log.operation_type,
          remarks: log.remarks,
          prodTime: log.prodtime,
          idleTime: log.idletime,
          mealTime: log.mealtime,
          finishedGoodStatus: log.finished_good_status,
          jobStatus: log.job_status,
          isApproved: log.is_approved,
          approvedAt: log.approved_at
            ? dayjs(log.approved_at)
              .add(AUDIT_TIMESTAMP_ADJUSTMENT_MINUTES, 'minute')
              .format('YYYY-MM-DD HH:mm:ss')
            : null,
          createdAt: log.created_at
            ? dayjs(log.created_at)
              .add(AUDIT_TIMESTAMP_ADJUSTMENT_MINUTES, 'minute')
              .format('YYYY-MM-DD HH:mm:ss')
            : '—',
          updatedAt: log.updated_at
            ? dayjs(log.updated_at)
              .add(AUDIT_TIMESTAMP_ADJUSTMENT_MINUTES, 'minute')
              .format('YYYY-MM-DD HH:mm:ss')
            : '—',
        }))
      );
    } catch {
      triggerSnackbar('Error fetching detail logs', 'error');
    }
  }, [machineCode, getCurrentTimeRange, triggerSnackbar]);

  useEffect(() => {
    if (!dialogOpen || !machineCode) return;
    const fetchData = async () => {
      const now = Date.now();
      if (isFetchingRef.current || now - lastFetchTimeRef.current < 1000) return;
      isFetchingRef.current = true;
      lastFetchTimeRef.current = now;
      setLoading(true);
      try {
        await Promise.all([fetchManShiftLog(), fetchManDetailLog()]);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };
    fetchData();
  }, [dialogOpen, machineCode, selectedTimeRange, fetchManShiftLog, fetchManDetailLog]);

  useEffect(() => {
    const grouped = manDetailLogs.reduce((acc, log) => {
      const key = `${log.supervisor}  ${log.employee}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(log);
      return acc;
    }, {});
    const tabs = Object.keys(grouped);
    if (detailActiveTab >= tabs.length && tabs.length > 0) setDetailActiveTab(0);
  }, [manDetailLogs, detailActiveTab]);


  const buildRowsWithConditionalGrouping = useCallback((rows) => {
    const NO_KEY = '__NO_KEY__';
    const getStartDT = (r) => r.startTime || r.start_datetime || r.startDateTime || r.start;

    // 1) sort by jobStartedTimeRaw || startTime
    const sorted = [...rows].sort((a, b) => {
      const ak = a.jobStartedTimeRaw || getStartDT(a) || NO_KEY;
      const bk = b.jobStartedTimeRaw || getStartDT(b) || NO_KEY;

      const ta = ak !== NO_KEY ? new Date(ak).getTime() : 0;
      const tb = bk !== NO_KEY ? new Date(bk).getTime() : 0;
      if (ta !== tb) return ta - tb;

      const sa = getStartDT(a) ? new Date(getStartDT(a)).getTime() : 0;
      const sb = getStartDT(b) ? new Date(getStartDT(b)).getTime() : 0;
      if (sa !== sb) return sa - sb;

      const ida = String(a.id ?? a._id ?? a.key ?? '');
      const idb = String(b.id ?? b._id ?? b.key ?? '');
      return ida.localeCompare(idb);
    });

    // 2) count only real groups (non-null jobStartedTimeRaw)
    const jstCounts = new Map();
    for (const r of sorted) {
      if (!r.jobStartedTimeRaw) continue;
      jstCounts.set(r.jobStartedTimeRaw, (jstCounts.get(r.jobStartedTimeRaw) || 0) + 1);
    }

    // 3) annotate rows
    let prevGroupKey = null;
    return sorted.map((r) => {
      const canGroup = !!r.jobStartedTimeRaw && (jstCounts.get(r.jobStartedTimeRaw) || 0) >= 2;
      const groupKey = canGroup ? r.jobStartedTimeRaw : null;
      const groupStart = canGroup && groupKey !== prevGroupKey;
      if (canGroup) prevGroupKey = groupKey; else prevGroupKey = null;

      return {
        ...r,
        __grouped: canGroup,
        __groupStart: groupStart,
        __groupLabel: canGroup ? dayjs(r.jobStartedTimeRaw).format('YYYY-MM-DD HH:mm:ss') : '',
        __groupSize: canGroup ? jstCounts.get(r.jobStartedTimeRaw) : 0,
      };
    });
  }, []);


  const handleApprove = useCallback(async (logs) => {
    // Store logs and open confirmation dialog instead of window.confirm
    setPendingApprovalLogs(logs);
    setApproveConfirmOpen(true);
  }, []);



  const handleConfirmApprove = useCallback(async () => {
    try {
      // ✅ Get ALL record IDs from the pending logs (not just JOB_STARTED)
      const idsToApprove = pendingApprovalLogs.map((log) => log.id);

      if (idsToApprove.length === 0) {
        triggerSnackbar("No records to approve.", "info");
        setApproveConfirmOpen(false);
        return;
      }

      // Send all IDs to backend
      const res = await axios.post(`${API_BASE_URL}/manhour-approve`, {
        recordIds: idsToApprove
      });

      triggerSnackbar(res.data.message || "All records approved successfully", "success");

      // ✅ Update ALL records in frontend state
      setManDetailLogs((prev) =>
        prev.map((log) =>
          idsToApprove.includes(log.id)
            ? {
              ...log,
              isApproved: 1,
              approvedAt: dayjs().format("YYYY-MM-DD HH:mm:ss.SSS")
            }
            : log
        )
      );

      setApproveConfirmOpen(false);
      setPendingApprovalLogs([]);
    } catch (err) {
      console.error("❌ Error approving records:", err);
      triggerSnackbar("Failed to approve records", "error");
      setApproveConfirmOpen(false);
    }
  }, [pendingApprovalLogs, triggerSnackbar]);



  const renderTabContent = useCallback(() => {
    const filterData = (data) =>
      !searchTerm
        ? data
        : data.filter((item) =>
          Object.values(item).some((v) =>
            v?.toString().toLowerCase().includes(searchTerm.toLowerCase())
          )
        );

    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }

    const gridCommonSX = {
      '& .MuiDataGrid-cell': { fontSize: '13px', py: 0.5 },
      '& .MuiDataGrid-columnHeader': {
        py: 0.5,
        fontWeight: 800,
        bgcolor: alpha(theme.palette.primary.main, 0.06),
      },
      bgcolor: alpha(theme.palette.background.paper, 0.98),
      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      '& .activity-row-production': {
        bgcolor: alpha(theme.palette.success.main, 0.08),
        boxShadow: `inset 4px 0 0 ${theme.palette.success.main}`,
      },
      '& .activity-row-idle': {
        bgcolor: alpha(theme.palette.warning.main, 0.10),
        boxShadow: `inset 4px 0 0 ${theme.palette.warning.main}`,
      },
      '& .activity-row-meal': {
        bgcolor: alpha(theme.palette.info.main, 0.08),
        boxShadow: `inset 4px 0 0 ${theme.palette.info.main}`,
      },
      '& .activity-row-other': {
        bgcolor: alpha(theme.palette.secondary.main, 0.07),
        boxShadow: `inset 4px 0 0 ${theme.palette.secondary.main}`,
      },
      '& .activity-row-session': {
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        boxShadow: `inset 4px 0 0 ${theme.palette.primary.main}`,
        fontWeight: 600,
      },
      '& .activity-row-non-commanded': {
        bgcolor: alpha(theme.palette.error.main, 0.13),
        boxShadow: `inset 5px 0 0 ${theme.palette.error.main}`,
        color: theme.palette.error.dark,
        fontWeight: 700,
      },
      '& .activity-row-production:hover': {
        bgcolor: alpha(theme.palette.success.main, 0.15),
      },
      '& .activity-row-idle:hover': {
        bgcolor: alpha(theme.palette.warning.main, 0.18),
      },
      '& .activity-row-meal:hover': {
        bgcolor: alpha(theme.palette.info.main, 0.15),
      },
      '& .activity-row-other:hover': {
        bgcolor: alpha(theme.palette.secondary.main, 0.14),
      },
      '& .activity-row-session:hover': {
        bgcolor: alpha(theme.palette.primary.main, 0.12),
      },
      '& .activity-row-non-commanded:hover': {
        bgcolor: alpha(theme.palette.error.main, 0.20),
      },
    };

    switch (activeTab) {
      case 0:
        return (
          <Box sx={{ height: '100%' }}>
            <DataGrid
              rows={filterData(manShiftLogs)}
              columns={shiftColumns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              pagination
              density="compact"
              disableColumnMenu
              sx={gridCommonSX}
              autoHeight={false}
            />
          </Box>
        );


      case 1: {
        // Filter detail logs first
        const filteredDetails = filterData(manDetailLogs);

        // ✅ Handle empty state
        if (filteredDetails.length === 0) {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                px: 3,
                minHeight: 400,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <AssignmentIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
              </Box>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary" gutterBottom>
                No Operation Details Found
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 400 }}>
                {searchTerm
                  ? `No operations match your search criteria "${searchTerm}"`
                  : `No operation details available for ${timeRanges[selectedTimeRange]?.label || 'the selected time range'}`}
              </Typography>
              {searchTerm && (
                <Button
                  onClick={() => setSearchTerm('')}
                  variant="outlined"
                  size="small"
                  sx={{ mt: 3, textTransform: 'none' }}
                >
                  Clear Search
                </Button>
              )}
            </Box>
          );
        }

        // Grouped Detail Logs (only if data exists)
        const groupedByEmployee = filteredDetails.reduce((acc, log) => {
          const key = `${log.supervisor}  ${log.employee}`;
          if (!acc[key]) acc[key] = [];
          acc[key].push(log);
          return acc;
        }, {});
        const employeeTabs = Object.keys(groupedByEmployee);

        return (
          <Box>
            <Tabs
              value={detailActiveTab}
              onChange={(e, v) => setDetailActiveTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              {employeeTabs.map((label, i) => (
                <Tab key={i} label={label} />
              ))}
            </Tabs>

            {employeeTabs.length > 0 && (() => {
              // 1ï¸ âƒ£ Filter + group rows
              const filteredLogs = filterData(groupedByEmployee[employeeTabs[detailActiveTab]] || []);
              const annotatedRows = buildRowsWithConditionalGrouping(filteredLogs);

              // 2ï¸ âƒ£ Filter out collapsed groups
              const visibleRows = annotatedRows.filter((r) => {
                if (!r.__grouped) return true;
                if (!r.jobStartedTimeRaw) return true;
                if (expandedGroup === r.jobStartedTimeRaw) return true;
                return r.__groupStart; // collapsed  show only header
              });


              return (
                <Box sx={{ mt: 2 }}>
                  <DataGrid
                    rows={visibleRows}
                    columns={[groupColumn, ...detailColumns]}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    pagination
                    density="compact"
                    disableColumnMenu
                    autoHeight={false}
                    getRowHeight={(params) => (params.model.__groupStart ? 56 : 'auto')}
                    getRowClassName={(params) =>
                      highlightedRows.includes(params.id)
                        ? 'row-highlight'
                        : params.row.__grouped
                          ? params.row.__groupStart
                            ? 'group-start'
                            : 'group-member'
                          : ''
                    }
                    sx={{
                      '& .MuiDataGrid-cell': { fontSize: '13px', py: 0.75 },
                      '& .MuiDataGrid-columnHeader': { py: 0.5 },
                      bgcolor: alpha(theme.palette.background.paper, 0.98),
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      '& .row-highlight': {
                        backgroundColor: alpha(theme.palette.success.light, 0.4),
                        transition: 'background-color 0.5s ease',
                      },
                      '& .group-start': {
                        borderTop: (theme) => `2px solid ${theme.palette.divider}`,
                        backgroundImage: (theme) =>
                          `linear-gradient(to bottom, ${theme.palette.action.hover} 0%, transparent 16px)`,
                        '&:hover': {
                          backgroundColor: (theme) =>
                            alpha(theme.palette.action.selected, 0.2),
                        },
                      },
                      '& .group-member': {
                        boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.action.hover}`,
                      },
                    }}
                  />
                </Box>
              );
            })()}
          </Box>
        );
      }





      case 2: {
        // Employee-wise analysis: whole selected period or individual job sessions.
        const filteredDetails = filterData(manDetailLogs);
        const groupedByEmployee = filteredDetails.reduce((acc, log) => {
          const employeeKey = String(log.empId || log.employee || 'unknown');
          if (!acc[employeeKey]) {
            acc[employeeKey] = {
              label: log.employee || 'Unknown Employee',
              logs: [],
            };
          }
          acc[employeeKey].logs.push(log);
          return acc;
        }, {});

        const employeeEntries = Object.entries(groupedByEmployee).sort(
          ([, first], [, second]) => first.label.localeCompare(second.label)
        );
        const selectedEmployeeIndex = Math.min(
          employeeAnalysisTab,
          Math.max(employeeEntries.length - 1, 0)
        );
        const selectedEmployeeEntry = employeeEntries[selectedEmployeeIndex];

        if (employeeEntries.length === 0) {
          return (
            <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
              <GroupIcon sx={{ fontSize: 42, mb: 1, opacity: 0.6 }} />
              <Typography fontWeight={700}>No employee activity found</Typography>
              <Typography variant="body2">
                Change the time range or clear the current search.
              </Typography>
            </Box>
          );
        }

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: { xs: 'stretch', md: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 1.5,
                borderRadius: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={800}>
                  Employee Activity Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {timeRanges[selectedTimeRange]?.label || 'Selected period'} · categorized working and unrecorded time
                </Typography>
              </Box>
              <ButtonGroup size="small" variant="outlined">
                <Button
                  variant={employeeViewMode === 'all' ? 'contained' : 'outlined'}
                  onClick={() => setEmployeeViewMode('all')}
                >
                  All-in-One
                </Button>
                <Button
                  variant={employeeViewMode === 'period' ? 'contained' : 'outlined'}
                  onClick={() => setEmployeeViewMode('period')}
                >
                  Whole Period
                </Button>
                <Button
                  variant={employeeViewMode === 'shift' ? 'contained' : 'outlined'}
                  onClick={() => setEmployeeViewMode('shift')}
                >
                  By Shift Session
                </Button>
                <Button
                  variant={employeeViewMode === 'job' ? 'contained' : 'outlined'}
                  onClick={() => setEmployeeViewMode('job')}
                >
                  By Job Session
                </Button>
              </ButtonGroup>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.98),
                backdropFilter: 'blur(8px)',
                overflow: 'hidden',
              }}
            >
              <Tabs
                value={selectedEmployeeIndex}
                onChange={(event, newValue) => setEmployeeAnalysisTab(newValue)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                aria-label="Employee analysis tabs"
                sx={{
                  minHeight: 52,
                  '& .MuiTab-root': {
                    minHeight: 52,
                    textTransform: 'none',
                    minWidth: 170,
                    alignItems: 'flex-start',
                  },
                }}
              >
                {employeeEntries.map(([employeeKey, employeeGroup]) => {
                  const employeeLogs = employeeGroup.logs;
                  const employeeActive = employeeLogs.some((row) => !row.endTimeRaw);
                  const employeeJobs = new Set(
                    employeeLogs
                      .map((row) => String(row.jobNumber || '').trim())
                      .filter(Boolean)
                  ).size;

                  return (
                    <Tab
                      key={employeeKey}
                      label={
                        <Box
                          sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              noWrap
                              sx={{ maxWidth: 130 }}
                            >
                              {employeeGroup.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {employeeJobs} job(s) · {employeeLogs.length} record(s)
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              flexShrink: 0,
                              bgcolor: employeeActive
                                ? theme.palette.success.main
                                : theme.palette.grey[400],
                            }}
                          />
                        </Box>
                      }
                    />
                  );
                })}
              </Tabs>
            </Paper>

            {selectedEmployeeEntry && [selectedEmployeeEntry].map(([employeeKey, employeeGroup]) => {
              const logs = employeeGroup.logs;
              const employeeShifts = manShiftLogs.filter(
                (shift) => String(shift.empId || '') === String(logs[0]?.empId || '')
              );
              const shiftSeconds = employeeShifts.reduce(
                (sum, shift) => sum + secondsBetween(shift.shiftInRaw, shift.shiftOutRaw),
                0
              );
              const jobRows = logs.filter(
                (log) => log.operationDescription === 'JOB_STARTED'
              );
              const recordedJobSeconds = jobRows.reduce(
                (sum, row) => sum + rowDurationSeconds(row),
                0
              );
              const standaloneSeconds = logs
                .filter(
                  (row) =>
                    row.operationDescription !== 'JOB_STARTED' &&
                    !row.jobStartedTimeRaw
                )
                .reduce((sum, row) => sum + rowDurationSeconds(row), 0);
              const totalWindowSeconds =
                shiftSeconds > 0
                  ? shiftSeconds
                  : recordedJobSeconds + standaloneSeconds;
              const metrics = employeeShifts.length > 0
                ? (() => {
                    const shiftMetrics = employeeShifts.map((shift) => {
                      const shiftLogs = getLogsOverlappingWindow(
                        logs,
                        shift.shiftInRaw,
                        shift.shiftOutRaw
                      );
                      const shiftDuration = secondsBetween(
                        shift.shiftInRaw,
                        shift.shiftOutRaw
                      );
                      return calculateCategorizedTimeMetrics(
                        shiftLogs,
                        shiftDuration,
                        shift.shiftInRaw,
                        shift.shiftOutRaw
                      );
                    });
                    const totalSeconds = shiftMetrics.reduce(
                      (sum, item) => sum + item.totalSeconds,
                      0
                    );
                    const productionSeconds = shiftMetrics.reduce(
                      (sum, item) => sum + item.productionSeconds,
                      0
                    );
                    const idleSeconds = shiftMetrics.reduce(
                      (sum, item) => sum + item.idleSeconds,
                      0
                    );
                    const mealSeconds = shiftMetrics.reduce(
                      (sum, item) => sum + item.mealSeconds,
                      0
                    );
                    const otherSeconds = shiftMetrics.reduce(
                      (sum, item) => sum + item.otherSeconds,
                      0
                    );
                    const commandedSeconds =
                      productionSeconds + idleSeconds + mealSeconds + otherSeconds;
                    const nonCommandedSeconds = Math.max(
                      totalSeconds - commandedSeconds,
                      0
                    );

                    return {
                      totalSeconds,
                      commandedSeconds,
                      nonCommandedSeconds,
                      productionSeconds,
                      idleSeconds,
                      mealSeconds,
                      otherSeconds,
                      total: formatDurationSeconds(totalSeconds),
                      production: formatDurationSeconds(productionSeconds),
                      idle: formatDurationSeconds(idleSeconds),
                      meal: formatDurationSeconds(mealSeconds),
                      other: formatDurationSeconds(otherSeconds),
                      nonCommanded: formatDurationSeconds(nonCommandedSeconds),
                      commandedRate: totalSeconds > 0
                        ? Math.min(100, (commandedSeconds / totalSeconds) * 100).toFixed(1)
                        : '0.0',
                    };
                  })()
                : calculateCategorizedTimeMetrics(logs, totalWindowSeconds);
              const uniqueJobs = new Set(
                logs.map((row) => String(row.jobNumber || '').trim()).filter(Boolean)
              );
              const activeCount = logs.filter((row) => !row.endTimeRaw).length;
              const totalProd = jobRows.reduce(
                (sum, row) => sum + (Number(row.prodQty) || 0),
                0
              );
              const totalWaste = jobRows.reduce(
                (sum, row) => sum + (Number(row.wasteQty) || 0),
                0
              );

              const sessionGroups = logs.reduce((acc, row) => {
                if (!row.jobStartedTimeRaw || !String(row.jobNumber || '').trim()) {
                  return acc;
                }
                const sessionKey = `${row.jobNumber}__${row.jobStartedTimeRaw}`;
                if (!acc[sessionKey]) acc[sessionKey] = [];
                acc[sessionKey].push(row);
                return acc;
              }, {});
              const nonJobLogs = logs.filter(
                (row) => !row.jobStartedTimeRaw || !String(row.jobNumber || '').trim()
              );

              const periodGaps = employeeShifts.length > 0
                ? employeeShifts.flatMap((shift) =>
                    calculateNonCommandedGaps(
                      getLogsOverlappingWindow(
                        logs,
                        shift.shiftInRaw,
                        shift.shiftOutRaw
                      ),
                      shift.shiftInRaw,
                      shift.shiftOutRaw
                    )
                  )
                : Object.values(sessionGroups).flatMap((sessionLogs) => {
                    const jobStartRow = sessionLogs.find(
                      (row) => row.operationDescription === 'JOB_STARTED'
                    );
                    return jobStartRow
                      ? calculateNonCommandedGaps(
                          sessionLogs,
                          jobStartRow.startTimeRaw || jobStartRow.jobStartedTimeRaw,
                          jobStartRow.endTimeRaw
                        )
                      : [];
                  });
              const periodGapSeconds = periodGaps.reduce(
                (sum, gap) => sum + gap.durationSeconds,
                0
              );
              const periodCommandedRate = metrics.totalSeconds > 0
                ? Math.max(
                    0,
                    Math.min(
                      100,
                      ((metrics.totalSeconds - periodGapSeconds) / metrics.totalSeconds) * 100
                    )
                  ).toFixed(1)
                : '0.0';


              return (
                <Paper
                  key={employeeKey}
                  elevation={1}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={800}>
                        {employeeGroup.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {logs[0]?.stationCode} · {employeeShifts.length} shift session(s) · {uniqueJobs.size} job(s) · {logs.length} record(s)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {activeCount > 0 && (
                        <Chip
                          size="small"
                          color="info"
                          label={`${activeCount} ongoing`}
                        />
                      )}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Production Qty ${totalProd}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        color="error"
                        label={`Waste ${totalWaste}`}
                      />
                    </Box>
                  </Box>

                  <Paper
                    variant="outlined"
                    sx={{
                      px: 1.25,
                      py: 1,
                      mb: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 0.75,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.background.default, 0.55),
                    }}
                  >
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`${shiftSeconds > 0 ? 'Shift' : 'Recorded'} ${metrics.total}`}
                    />
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={`Production ${metrics.production}`}
                    />
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={`Idle / Setup ${metrics.idle}`}
                    />
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      label={`Meal ${metrics.meal}`}
                    />
                    {metrics.otherSeconds > 0 && (
                      <Chip
                        size="small"
                        color="secondary"
                        variant="outlined"
                        label={`Other ${metrics.other}`}
                      />
                    )}
                    <Chip
                      size="small"
                      color="error"
                      variant={periodGapSeconds > 0 ? 'filled' : 'outlined'}
                      label={`Non-commanded ${formatDurationSeconds(periodGapSeconds)}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${periodCommandedRate}% categorized`}
                    />
                    {shiftSeconds === 0 && (
                      <Typography variant="caption" color="warning.main">
                        Shift log unavailable
                      </Typography>
                    )}
                  </Paper>

                  {employeeViewMode === 'all' ? (() => {
                    const sortedShifts = [...employeeShifts].sort(
                      (first, second) =>
                        new Date(first.shiftInRaw).getTime() -
                        new Date(second.shiftInRaw).getTime()
                    );
                    const nowMs = Date.now();

                    const getAssignedShiftIndex = (row) => {
                      if (sortedShifts.length === 0) return -1;

                      const rowStartMs = new Date(row.startTimeRaw).getTime();
                      const rowEndMs = row.endTimeRaw
                        ? new Date(row.endTimeRaw).getTime()
                        : nowMs;
                      if (!Number.isFinite(rowStartMs) || !Number.isFinite(rowEndMs)) {
                        return -1;
                      }

                      const containingIndex = sortedShifts.findIndex((shift) => {
                        const shiftStartMs = new Date(shift.shiftInRaw).getTime();
                        const shiftEndMs = shift.shiftOutRaw
                          ? new Date(shift.shiftOutRaw).getTime()
                          : nowMs;
                        return rowStartMs >= shiftStartMs && rowStartMs < shiftEndMs;
                      });
                      if (containingIndex >= 0) return containingIndex;

                      return sortedShifts.findIndex((shift) => {
                        const shiftStartMs = new Date(shift.shiftInRaw).getTime();
                        const shiftEndMs = shift.shiftOutRaw
                          ? new Date(shift.shiftOutRaw).getTime()
                          : nowMs;
                        return rowStartMs < shiftEndMs && rowEndMs > shiftStartMs;
                      });
                    };

                    const createSegmentRows = ({
                      sourceLogs,
                      gaps,
                      shiftKey,
                      shiftNumber,
                      shiftName,
                      shiftDate,
                      shiftTime,
                      windowStart = null,
                      windowEnd = null,
                    }) => {
                      const jobSessions = sourceLogs
                        .filter((row) =>
                          row.operationDescription === 'JOB_STARTED' &&
                          row.jobStartedTimeRaw &&
                          String(row.jobNumber || '').trim()
                        )
                        .map((row) => ({
                          key: `${row.jobNumber}__${row.jobStartedTimeRaw}`,
                          jobNumber: row.jobNumber,
                          startRaw: row.startTimeRaw || row.jobStartedTimeRaw,
                          startMs: new Date(row.startTimeRaw || row.jobStartedTimeRaw).getTime(),
                          endMs: row.endTimeRaw
                            ? new Date(row.endTimeRaw).getTime()
                            : nowMs,
                        }))
                        .filter((session) => Number.isFinite(session.startMs))
                        .sort((first, second) => first.startMs - second.startMs);

                      const resolveJobSession = (row) => {
                        if (row.jobStartedTimeRaw && String(row.jobNumber || '').trim()) {
                          const directKey = `${row.jobNumber}__${row.jobStartedTimeRaw}`;
                          const directSession = jobSessions.find(
                            (session) => session.key === directKey
                          );
                          if (directSession) return directSession;
                        }

                        const rowTime = new Date(row.startTimeRaw || row.startTime).getTime();
                        if (!Number.isFinite(rowTime)) return null;

                        return [...jobSessions]
                          .reverse()
                          .find(
                            (session) =>
                              rowTime >= session.startMs && rowTime <= session.endMs
                          ) || null;
                      };

                      return buildTimelineRows(
                        sourceLogs,
                        gaps,
                        windowStart,
                        windowEnd
                      ).map((row) => {
                        const jobSession = resolveJobSession(row);
                        return {
                          ...row,
                          id: row.isNonCommandedGap
                            ? `${shiftKey}__${row.id}`
                            : row.id,
                          __shiftKey: shiftKey,
                          __shiftNumber: shiftNumber,
                          __shiftName: shiftName,
                          __shiftDate: shiftDate,
                          __shiftTime: shiftTime,
                          __jobSessionKey: jobSession?.key || `${shiftKey}__outside-job`,
                          __jobSessionLabel: jobSession
                            ? `Job ${jobSession.jobNumber} · ${dayjs(jobSession.startRaw).format('DD MMM HH:mm')}`
                            : 'Outside Job / Shift Activity',
                        };
                      });
                    };

                    let allInOneRows = [];
                    let totalJobSessionCount = 0;

                    if (sortedShifts.length > 0) {
                      sortedShifts.forEach((shift, shiftIndex) => {
                        const shiftLogs = logs.filter(
                          (row) => getAssignedShiftIndex(row) === shiftIndex
                        );
                        const shiftGaps = calculateNonCommandedGaps(
                          shiftLogs,
                          shift.shiftInRaw,
                          shift.shiftOutRaw
                        );
                        totalJobSessionCount += shiftLogs.filter(
                          (row) => row.operationDescription === 'JOB_STARTED'
                        ).length;

                        allInOneRows.push(
                          ...createSegmentRows({
                            sourceLogs: shiftLogs,
                            gaps: shiftGaps,
                            shiftKey: `shift-${shift.id || shiftIndex}`,
                            shiftNumber: shiftIndex + 1,
                            shiftName: `Shift ${shiftIndex + 1}`,
                            shiftDate: dayjs(shift.shiftInRaw).format('DD MMM'),
                            shiftTime: `${dayjs(shift.shiftInRaw).format('HH:mm')}–${
                              shift.shiftOutRaw
                                ? dayjs(shift.shiftOutRaw).format('HH:mm')
                                : 'Now'
                            }`,
                            windowStart: shift.shiftInRaw,
                            windowEnd: shift.shiftOutRaw,
                          })
                        );
                      });

                      const outsideShiftLogs = logs.filter(
                        (row) => getAssignedShiftIndex(row) === -1
                      );
                      if (outsideShiftLogs.length > 0) {
                        totalJobSessionCount += outsideShiftLogs.filter(
                          (row) => row.operationDescription === 'JOB_STARTED'
                        ).length;
                        allInOneRows.push(
                          ...createSegmentRows({
                            sourceLogs: outsideShiftLogs,
                            gaps: [],
                            shiftKey: 'outside-shift',
                            shiftNumber: null,
                            shiftName: 'Outside Shift',
                            shiftDate: '',
                            shiftTime: 'No shift log',
                          })
                        );
                      }
                    } else {
                      totalJobSessionCount = jobRows.length;
                      allInOneRows = createSegmentRows({
                        sourceLogs: logs,
                        gaps: periodGaps,
                        shiftKey: 'recorded-period',
                        shiftNumber: null,
                        shiftName: 'Recorded Period',
                        shiftDate: '',
                        shiftTime: 'Shift unavailable',
                      });
                    }

                    allInOneRows.sort((first, second) => {
                      const firstTime = new Date(first.startTimeRaw || first.startTime).getTime();
                      const secondTime = new Date(second.startTimeRaw || second.startTime).getTime();
                      return firstTime - secondTime;
                    });

                    let previousShiftKey = null;
                    let previousJobSessionKey = null;
                    allInOneRows = allInOneRows.map((row) => {
                      const shiftStart = row.__shiftKey !== previousShiftKey;
                      const jobSessionStart =
                        shiftStart || row.__jobSessionKey !== previousJobSessionKey;
                      previousShiftKey = row.__shiftKey;
                      previousJobSessionKey = row.__jobSessionKey;

                      return {
                        ...row,
                        __shiftStart: shiftStart,
                        __jobSessionStart: jobSessionStart,
                      };
                    });

                    const shiftColumn = {
                      field: '__shiftName',
                      headerName: 'Shift',
                      width: 86,
                      minWidth: 86,
                      maxWidth: 86,
                      sortable: false,
                      filterable: false,
                      disableColumnMenu: true,
                      renderCell: (params) => (
                        <Box
                          sx={{
                            width: '100%',
                            minHeight: params.row.__shiftStart ? 76 : 34,
                            px: 0.4,
                            py: 0.5,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                            borderRadius: 1,
                            color: theme.palette.text.primary,
                            bgcolor: params.row.__shiftStart
                              ? alpha(theme.palette.grey[500], 0.14)
                              : 'transparent',
                          }}
                        >
                          {params.row.__shiftStart ? (
                            <>
                              <Typography
                                variant="caption"
                                fontWeight={900}
                                sx={{ fontSize: '0.65rem', lineHeight: 1.05 }}
                              >
                                {params.row.__shiftName}
                              </Typography>
                              {params.row.__shiftDate && (
                                <Typography sx={{ fontSize: '0.62rem', lineHeight: 1.1, mt: 0.35 }}>
                                  {params.row.__shiftDate}
                                </Typography>
                              )}
                              <Typography sx={{ fontSize: '0.58rem', lineHeight: 1.1, mt: 0.25 }}>
                                {params.row.__shiftTime}
                              </Typography>
                            </>
                          ) : (
                            <Typography
                              variant="caption"
                              fontWeight={800}
                              color="text.secondary"
                              sx={{ fontSize: '0.62rem' }}
                            >
                              {params.row.__shiftNumber
                                ? `S${params.row.__shiftNumber}`
                                : '—'}
                            </Typography>
                          )}
                        </Box>
                      ),
                    };

                    const jobSessionColumn = {
                      field: '__jobSessionLabel',
                      headerName: 'Job Session',
                      width: 205,
                      sortable: false,
                      filterable: false,
                      disableColumnMenu: true,
                      renderCell: (params) => (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={params.value}
                          sx={{
                            maxWidth: 190,
                            color: 'text.primary',
                            borderColor: alpha(theme.palette.grey[500], 0.45),
                            bgcolor: params.row.__jobSessionStart
                              ? alpha(theme.palette.grey[500], 0.12)
                              : 'transparent',
                            fontWeight: params.row.__jobSessionStart ? 800 : 500,
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            },
                          }}
                        />
                      ),
                    };

                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 1.25,
                            py: 1,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1,
                            bgcolor: alpha(theme.palette.background.default, 0.5),
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={800}>
                              All Shifts — Full Detail Sheet
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Narrow shift identifier at left · job sessions separated inside one continuous table
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${sortedShifts.length} shift session(s)`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${totalJobSessionCount} job session(s)`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${allInOneRows.length} table row(s)`}
                            />
                          </Box>
                        </Paper>

                        {allInOneRows.length > 0 ? (
                          <DataGrid
                            rows={allInOneRows}
                            columns={[shiftColumn, jobSessionColumn, ...detailColumns]}
                            pageSize={25}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            pagination
                            density="compact"
                            disableColumnMenu
                            autoHeight
                            getRowHeight={(params) =>
                              params.model.__shiftStart ? 92 : 'auto'
                            }
                            getRowClassName={(params) =>
                              `${getActivityRowClass(params.row, highlightedRows)} ${
                                params.row.__shiftStart ? 'all-in-one-shift-start' : ''
                              } ${
                                params.row.__jobSessionStart ? 'all-in-one-job-start' : ''
                              }`
                            }
                            sx={{
                              ...gridCommonSX,
                              '& .MuiDataGrid-cell': {
                                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                                py: 0.75,
                                alignItems: 'center',
                              },
                              '& .activity-row-production, & .activity-row-idle, & .activity-row-meal, & .activity-row-other, & .activity-row-session': {
                                bgcolor: 'transparent',
                                boxShadow: 'none',
                                color: 'inherit',
                              },
                              '& .activity-row-non-commanded': {
                                bgcolor: alpha(theme.palette.grey[500], 0.08),
                                boxShadow: 'none',
                                color: 'text.primary',
                                fontWeight: 700,
                              },
                              '& .activity-row-production:hover, & .activity-row-idle:hover, & .activity-row-meal:hover, & .activity-row-other:hover, & .activity-row-session:hover, & .activity-row-non-commanded:hover': {
                                bgcolor: alpha(theme.palette.grey[500], 0.1),
                              },
                              '& .all-in-one-shift-start': {
                                borderTop: `4px solid ${alpha(theme.palette.grey[700], 0.72)}`,
                              },
                              '& .all-in-one-job-start:not(.all-in-one-shift-start)': {
                                borderTop: `2px solid ${alpha(theme.palette.grey[500], 0.38)}`,
                              },
                            }}
                          />
                        ) : (
                          <Paper
                            variant="outlined"
                            sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed' }}
                          >
                            <Typography fontWeight={700}>No employee activity found</Typography>
                          </Paper>
                        )}
                      </Box>
                    );
                  })() : employeeViewMode === 'period' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <DataGrid
                        rows={buildTimelineRows(logs, periodGaps)}
                        columns={detailColumns}
                        pageSize={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        pagination
                        density="compact"
                        disableColumnMenu
                        autoHeight
                        getRowClassName={(params) =>
                          getActivityRowClass(params.row, highlightedRows)
                        }
                        sx={gridCommonSX}
                      />
                    </Box>
                  ) : employeeViewMode === 'shift' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {employeeShifts.length === 0 && (
                        <Paper
                          variant="outlined"
                          sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed' }}
                        >
                          <Typography fontWeight={700}>No shift session found</Typography>
                          <Typography variant="body2" color="text.secondary">
                            The activity records are available in Whole Period and By Job Session views.
                          </Typography>
                        </Paper>
                      )}

                      {employeeShifts.map((shift, shiftIndex) => {
                        const shiftLogs = getLogsOverlappingWindow(
                          logs,
                          shift.shiftInRaw,
                          shift.shiftOutRaw
                        );
                        const shiftKey = `shift__${employeeKey}__${shift.id || shiftIndex}`;
                        const expanded = !!expandedSubDetails[shiftKey];
                        const shiftDurationSeconds = secondsBetween(
                          shift.shiftInRaw,
                          shift.shiftOutRaw
                        );
                        const shiftMetrics = calculateCategorizedTimeMetrics(
                          shiftLogs,
                          shiftDurationSeconds,
                          shift.shiftInRaw,
                          shift.shiftOutRaw
                        );
                        const shiftGaps = calculateNonCommandedGaps(
                          shiftLogs,
                          shift.shiftInRaw,
                          shift.shiftOutRaw
                        );
                        const shiftGapSeconds = shiftGaps.reduce(
                          (sum, gap) => sum + gap.durationSeconds,
                          0
                        );
                        const shiftJobRows = shiftLogs.filter(
                          (row) => row.operationDescription === 'JOB_STARTED'
                        );
                        const shiftJobs = new Set(
                          shiftLogs
                            .map((row) => String(row.jobNumber || '').trim())
                            .filter(Boolean)
                        );
                        const shiftProdQty = shiftJobRows.reduce(
                          (sum, row) => sum + (Number(row.prodQty) || 0),
                          0
                        );
                        const shiftWasteQty = shiftJobRows.reduce(
                          (sum, row) => sum + (Number(row.wasteQty) || 0),
                          0
                        );

                        return (
                          <Paper
                            key={shiftKey}
                            variant="outlined"
                            sx={{ borderRadius: 2, overflow: 'hidden' }}
                          >
                            <Box
                              sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setExpandedSubDetails((prev) => ({
                                    ...prev,
                                    [shiftKey]: !prev[shiftKey],
                                  }))
                                }
                              >
                                {expanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                              </IconButton>
                              <Box sx={{ flex: 1, minWidth: 220 }}>
                                <Typography variant="body2" fontWeight={800}>
                                  Shift Session {shiftIndex + 1}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {shift.shiftIn} → {shift.shiftOut || 'Ongoing'} · {shiftJobs.size} job(s) · {shiftLogs.length} activity record(s)
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                                <Chip size="small" label={`Shift ${shiftMetrics.total}`} />
                                <Chip size="small" color="success" variant="outlined" label={`Prod ${shiftMetrics.production}`} />
                                <Chip size="small" color="warning" variant="outlined" label={`Idle ${shiftMetrics.idle}`} />
                                <Chip size="small" color="info" variant="outlined" label={`Meal ${shiftMetrics.meal}`} />
                                <Chip size="small" color="error" variant="outlined" label={`Non-commanded ${formatDurationSeconds(shiftGapSeconds)}`} />
                                <Chip size="small" color="error" label={`${shiftGaps.length} gap(s)`} />
                                <Chip size="small" variant="outlined" label={`Qty ${shiftProdQty} / Waste ${shiftWasteQty}`} />
                              </Box>
                            </Box>
                            <Collapse in={expanded} unmountOnExit>
                              <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {shiftLogs.length > 0 ? (
                                  <DataGrid
                                    rows={buildTimelineRows(
                                      shiftLogs,
                                      shiftGaps,
                                      shift.shiftInRaw,
                                      shift.shiftOutRaw
                                    )}
                                    columns={detailColumns}
                                    density="compact"
                                    getRowClassName={(params) =>
                                      getActivityRowClass(params.row, highlightedRows)
                                    }
                                    disableColumnMenu
                                    autoHeight
                                    hideFooter={shiftLogs.length <= 10}
                                    sx={gridCommonSX}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No detail activity was recorded inside this shift window.
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </Paper>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {Object.entries(sessionGroups).map(([sessionKey, sessionLogs]) => {
                        const jobStartRow =
                          sessionLogs.find(
                            (row) => row.operationDescription === 'JOB_STARTED'
                          ) || sessionLogs[0];
                        const sessionWindowSeconds = jobStartRow
                          ? rowDurationSeconds(jobStartRow)
                          : 0;
                        const sessionMetrics = calculateCategorizedTimeMetrics(
                          sessionLogs,
                          sessionWindowSeconds,
                          jobStartRow.startTimeRaw || jobStartRow.jobStartedTimeRaw,
                          jobStartRow.endTimeRaw
                        );
                        const sessionGaps = calculateNonCommandedGaps(
                          sessionLogs,
                          jobStartRow.startTimeRaw || jobStartRow.jobStartedTimeRaw,
                          jobStartRow.endTimeRaw
                        );
                        const sessionGapSeconds = sessionGaps.reduce(
                          (sum, gap) => sum + gap.durationSeconds,
                          0
                        );
                        const expanded = !!expandedSubDetails[sessionKey];

                        return (
                          <Paper
                            key={sessionKey}
                            variant="outlined"
                            sx={{ borderRadius: 2, overflow: 'hidden' }}
                          >
                            <Box
                              sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setExpandedSubDetails((prev) => ({
                                    ...prev,
                                    [sessionKey]: !prev[sessionKey],
                                  }))
                                }
                              >
                                {expanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                              </IconButton>
                              <Box sx={{ flex: 1, minWidth: 220 }}>
                                <Typography variant="body2" fontWeight={800}>
                                  Job {jobStartRow.jobNumber}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {jobStartRow.customer || 'No customer'} · {jobStartRow.product || 'No product'} · {jobStartRow.mainOperation || 'No main operation'}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {jobStartRow.startTime || jobStartRow.jobStartedTime} → {jobStartRow.endTime || 'Ongoing'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                                {jobStartRow.upCount && (
                                  <Chip size="small" label={`Ups ${jobStartRow.upCount}`} />
                                )}
                                <Chip size="small" label={`Total ${sessionMetrics.total}`} />
                                <Chip size="small" color="success" variant="outlined" label={`Prod ${sessionMetrics.production}`} />
                                <Chip size="small" color="warning" variant="outlined" label={`Idle ${sessionMetrics.idle}`} />
                                <Chip size="small" color="info" variant="outlined" label={`Meal ${sessionMetrics.meal}`} />
                                <Chip size="small" color="error" variant="outlined" label={`Non-commanded ${formatDurationSeconds(sessionGapSeconds)}`} />
                                <Chip size="small" color="error" label={`${sessionGaps.length} gap(s)`} />
                              </Box>
                            </Box>
                            <Collapse in={expanded} unmountOnExit>
                              <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <DataGrid
                                  rows={buildTimelineRows(
                                    sessionLogs,
                                    sessionGaps,
                                    jobStartRow.startTimeRaw || jobStartRow.jobStartedTimeRaw,
                                    jobStartRow.endTimeRaw
                                  )}
                                  columns={detailColumns}
                                  density="compact"
                                  getRowClassName={(params) =>
                                    getActivityRowClass(params.row, highlightedRows)
                                  }
                                  disableColumnMenu
                                  autoHeight
                                  hideFooter={sessionLogs.length <= 10}
                                  sx={gridCommonSX}
                                />
                              </Box>
                            </Collapse>
                          </Paper>
                        );
                      })}

                      {nonJobLogs.length > 0 && (() => {
                        const nonJobKey = `non-job__${employeeKey}`;
                        const expanded = !!expandedSubDetails[nonJobKey];
                        const nonJobMetrics = calculateCategorizedTimeMetrics(
                          nonJobLogs,
                          nonJobLogs.reduce(
                            (sum, row) => sum + rowDurationSeconds(row),
                            0
                          )
                        );

                        return (
                          <Paper
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              overflow: 'hidden',
                              borderColor: alpha(theme.palette.warning.main, 0.45),
                            }}
                          >
                            <Box
                              sx={{
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                bgcolor: alpha(theme.palette.warning.main, 0.06),
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setExpandedSubDetails((prev) => ({
                                    ...prev,
                                    [nonJobKey]: !prev[nonJobKey],
                                  }))
                                }
                              >
                                {expanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                              </IconButton>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight={800}>
                                  Outside Job / Non-job Activities
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {nonJobLogs.length} record(s)
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                <Chip size="small" color="warning" variant="outlined" label={`Idle ${nonJobMetrics.idle}`} />
                                <Chip size="small" color="info" variant="outlined" label={`Meal ${nonJobMetrics.meal}`} />
                                <Chip size="small" variant="outlined" label={`Other ${nonJobMetrics.other}`} />
                              </Box>
                            </Box>
                            <Collapse in={expanded} unmountOnExit>
                              <Box sx={{ p: 1.5 }}>
                                <DataGrid
                                  rows={buildTimelineRows(nonJobLogs)}
                                  columns={nonJobDetailColumns}
                                  density="compact"
                                  getRowClassName={(params) =>
                                    getActivityRowClass(params.row, highlightedRows)
                                  }
                                  disableColumnMenu
                                  autoHeight
                                  hideFooter={nonJobLogs.length <= 10}
                                  sx={gridCommonSX}
                                />
                              </Box>
                            </Collapse>
                          </Paper>
                        );
                      })()}
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>
        );
      }


      case 3: {
        // --- JOB SUMMARY TAB ---
        // Keep job records and non-job activities separated. Non-job activities
        // remain reviewable and approvable without creating a misleading job card.
        const visibleDetailLogs = filterData(manDetailLogs);
        const filteredLogs = visibleDetailLogs.filter(
          (log) => String(log.jobNumber || '').trim() !== ''
        );
        const nonJobLogs = visibleDetailLogs.filter(
          (log) => String(log.jobNumber || '').trim() === ''
        );
        const getNonJobStatus = (log) => {
          if (!log.endTime) return 'ongoing';
          return log.isApproved === 1 ? 'completed' : 'pending';
        };
        const filteredNonJobLogs = nonJobLogs.filter(
          (log) =>
            jobStatusFilter === 'all' ||
            getNonJobStatus(log) === jobStatusFilter
        );
        const nonJobEmployeeGroups = filteredNonJobLogs.reduce((acc, log) => {
          const employeeKey = log.employee || 'Unknown Employee';
          if (!acc[employeeKey]) acc[employeeKey] = [];
          acc[employeeKey].push(log);
          return acc;
        }, {});

        // Group by Job Number
        const jobsGrouped = filteredLogs.reduce((acc, log) => {
          const jobKey = String(log.jobNumber).trim();
          if (!acc[jobKey]) acc[jobKey] = [];
          acc[jobKey].push(log);
          return acc;
        }, {});

        const getJobStatus = (jobLogs) => {
          const hasOngoing = jobLogs.some(
            (log) => !log.endTime || log.endTime === '' || log.endTime === null
          );
          if (hasOngoing) return 'ongoing';

          const allApproved = jobLogs.length > 0 && jobLogs.every((log) => log.isApproved === 1);
          if (allApproved) return 'completed';

          return 'pending';
        };

        const allJobEntries = Object.entries(jobsGrouped);
        const filteredJobEntries = allJobEntries.filter(
          ([, jobLogs]) => jobStatusFilter === 'all' || getJobStatus(jobLogs) === jobStatusFilter
        );

        const handleJobStatusFilter = (status) => {
          setJobStatusFilter((current) =>
            status !== 'all' && current === status ? 'all' : status
          );
          setExpandedGroup(null);
        };

        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* ✅ NEW: Status Legend */}


            {/* ✅ MINIMIZED: Overview Dashboard - Compact 3-Row Version */}
            <Paper
              elevation={2}
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: `linear-gradient(90deg, 
                    ${theme.palette.info.main} 0%, 
                    ${theme.palette.success.main} 50%, 
                    ${theme.palette.warning.main} 100%)`,
                }
              }}
            >
              {/* Header - Minimized */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '6px',
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography fontSize="1rem">📊</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
                    Overview Dashboard
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Summary of jobs and non-job activities in selected time range
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`Showing ${filteredJobEntries.length + filteredNonJobLogs.length} of ${allJobEntries.length + nonJobLogs.length} results`}
                  sx={{
                    height: 24,
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.dark,
                  }}
                />
              </Box>

              {(() => {
                // Calculate summary statistics from all jobs
                const allJobs = allJobEntries;
                const totalJobs = allJobs.length;

                // Job status counts
                let ongoingJobsCount = 0;
                let completedJobsCount = 0;
                let pendingJobsCount = 0;

                // Record and employee counts
                let totalRecords = 0;
                let totalPendingRecords = 0;
                let totalApprovedRecords = 0;
                const uniqueEmployees = new Set();
                const uniqueJobStartTimes = new Set();

                // Production metrics
                let totalProductionQty = 0;
                let totalWasteQty = 0;

                // Process each job
                allJobs.forEach(([jobNumber, jobLogs]) => {
                  totalRecords += jobLogs.length;

                  // Check job status using the same rules as the clickable filter.
                  const jobStatus = getJobStatus(jobLogs);
                  if (jobStatus === 'ongoing') ongoingJobsCount++;
                  if (jobStatus === 'pending') pendingJobsCount++;
                  if (jobStatus === 'completed') completedJobsCount++;

                  // Count records by approval status
                  jobLogs.forEach((log) => {
                    if (log.isApproved === 1) {
                      totalApprovedRecords++;
                    } else {
                      totalPendingRecords++;
                    }

                    // Track employees and job start times
                    if (log.employee) uniqueEmployees.add(log.employee);
                    if (log.jobStartedTimeRaw) uniqueJobStartTimes.add(log.jobStartedTimeRaw);

                    // Sum production metrics
                    totalProductionQty += Number(log.prodQty) || 0;
                    totalWasteQty += Number(log.wasteQty) || 0;
                  });
                });

                // Include non-job records in the same overview status and approval totals.
                nonJobLogs.forEach((log) => {
                  const activityStatus = getNonJobStatus(log);
                  if (activityStatus === 'ongoing') ongoingJobsCount++;
                  if (activityStatus === 'pending') pendingJobsCount++;
                  if (activityStatus === 'completed') completedJobsCount++;

                  totalRecords++;
                  if (log.isApproved === 1) {
                    totalApprovedRecords++;
                  } else {
                    totalPendingRecords++;
                  }

                  if (log.employee) uniqueEmployees.add(log.employee);
                });

                const totalStatusItems = totalJobs + nonJobLogs.length;
                const totalEmployees = uniqueEmployees.size;
                const totalJobStarts = uniqueJobStartTimes.size;
                const approvalRate = totalRecords > 0 ? ((totalApprovedRecords / totalRecords) * 100).toFixed(1) : 0;
                const wasteRate = totalProductionQty > 0 ? ((totalWasteQty / (totalProductionQty + totalWasteQty)) * 100).toFixed(1) : 0;

                return (
                  <Paper
                    elevation={0}
                  // sx={{ 
                  //   p: 1, 
                  //   borderRadius: 1,
                  //   bgcolor: alpha(theme.palette.grey[50], 0.8),

                  // }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                        gap: 1,
                        alignItems: 'stretch',
                        width: '100%',
                      }}
                    >
                      {/* Job Status Section - clickable job-card filters */}
                      <Box sx={{
                        display: 'flex', alignItems: 'center', alignContent: 'center', gap: 0.75,
                        flexWrap: 'wrap', border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                        borderRadius: 1, p: 1, minWidth: 0,
                        bgcolor: alpha(theme.palette.grey[50], 0.8),
                      }}>
                        <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ fontSize: '0.75rem', mr: 0.25 }}>
                          📋 Activity Status:
                        </Typography>
                        {[
                          { value: 'all', label: 'Total', count: totalStatusItems, color: 'primary' },
                          { value: 'ongoing', label: '🔄 Ongoing', count: ongoingJobsCount, color: 'info' },
                          { value: 'pending', label: '⏱ Pending', count: pendingJobsCount, color: 'warning' },
                          { value: 'completed', label: '✓ Completed', count: completedJobsCount, color: 'success' },
                        ].map((item) => {
                          const selected = jobStatusFilter === item.value;
                          const paletteColor = theme.palette[item.color];
                          return (
                            <Tooltip key={item.value} title={`Show ${item.label.replace(/[🔄⏱✓]/g, '').trim()} jobs and non-job activities`}>
                              <Chip
                                clickable
                                size="small"
                                label={`${item.label}: ${item.count}`}
                                onClick={() => handleJobStatusFilter(item.value)}
                                aria-pressed={selected}
                                sx={{
                                  height: 25,
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                  color: selected ? paletteColor.contrastText : paletteColor.dark,
                                  bgcolor: selected ? paletteColor.main : alpha(paletteColor.main, 0.14),
                                  border: `1px solid ${selected ? paletteColor.dark : alpha(paletteColor.main, 0.32)}`,
                                  boxShadow: selected ? `0 2px 6px ${alpha(paletteColor.main, 0.3)}` : 'none',
                                  '&:hover': {
                                    bgcolor: selected ? paletteColor.dark : alpha(paletteColor.main, 0.24),
                                  },
                                }}
                              />
                            </Tooltip>
                          );
                        })}
                      </Box>


                      {/* Records Section */}
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', border: `1px solid ${alpha(theme.palette.divider, 0.15)}`, borderRadius: 1, p: 1,
                        bgcolor: alpha(theme.palette.grey[50], 0.8),
                      }}>
                        <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ fontSize: '0.75rem' }}>
                          📊 Records:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Total:</Typography>
                          <Chip label={totalRecords} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.dark, fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="error.dark" sx={{ fontSize: '0.7rem' }}>Pending:</Typography>
                          <Chip label={totalPendingRecords} size="small" sx={{ bgcolor: alpha(theme.palette.error.main, 0.2), color: theme.palette.error.dark, fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="success.dark" sx={{ fontSize: '0.7rem' }}>Approved:</Typography>
                          <Chip label={totalApprovedRecords} size="small" sx={{ bgcolor: alpha(theme.palette.success.main, 0.2), color: theme.palette.success.dark, fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="info.dark" sx={{ fontSize: '0.7rem' }}>Rate:</Typography>
                          <Chip label={`${approvalRate}%`} size="small" sx={{ bgcolor: alpha(theme.palette.info.main, 0.2), color: theme.palette.info.dark, fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                        </Box>
                      </Box>



                      {/* Production Section */}
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', border: `1px solid ${alpha(theme.palette.divider, 0.15)}`, borderRadius: 1, p: 1,
                        bgcolor: alpha(theme.palette.grey[50], 0.8),
                      }}>
                        <Typography variant="caption" fontWeight={700} color="text.primary" sx={{ fontSize: '0.75rem' }}>
                          📈 Production:
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <GroupIcon sx={{ fontSize: '0.8rem', color: theme.palette.secondary.main }} />
                          <Typography variant="caption" color="secondary.dark" sx={{ fontSize: '0.7rem' }}>Employees:</Typography>
                          <Chip label={totalEmployees} size="small" sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.2), color: theme.palette.secondary.dark, fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="primary.dark" sx={{ fontSize: '0.7rem' }}>Sessions:</Typography>
                          <Chip label={totalJobStarts} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.dark, fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="success.dark" sx={{ fontSize: '0.7rem' }}>✓ Prod:</Typography>
                          <Chip label={totalProductionQty.toLocaleString()} size="small" sx={{ bgcolor: alpha(theme.palette.success.main, 0.2), color: theme.palette.success.dark, fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="error.dark" sx={{ fontSize: '0.7rem' }}>⚠ Waste:</Typography>
                          <Chip label={totalWasteQty.toLocaleString()} size="small" sx={{ bgcolor: alpha(theme.palette.error.main, 0.2), color: theme.palette.error.dark, fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="error.dark" sx={{ fontSize: '0.7rem' }}>Rate:</Typography>
                          <Chip label={`${wasteRate}%`} size="small" sx={{ bgcolor: alpha(theme.palette.error.main, 0.25), color: theme.palette.error.dark, fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                );
              })()}
            </Paper>

            {filteredNonJobLogs.length > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  borderColor: alpha(theme.palette.divider, 0.8),
                }}
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    bgcolor: alpha(theme.palette.grey[500], 0.05),
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Non-job Activities
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Completed idle, setup, meal and general activities grouped by employee
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${filteredNonJobLogs.length} record${filteredNonJobLogs.length === 1 ? '' : 's'}`}
                  />
                </Box>

                <Box sx={{ p: 1 }}>
                  {Object.entries(nonJobEmployeeGroups).map(([employeeKey, employeeLogs]) => {
                    const employeeExpanded =
                      expandedSubGroups.__nonJobActivities === employeeKey;
                    const pendingCompletedLogs = employeeLogs.filter(
                      (log) =>
                        log.id !== null &&
                        log.id !== undefined &&
                        log.isApproved !== 1 &&
                        Boolean(log.endTime)
                    );
                    const approvedCount = employeeLogs.filter(
                      (log) => log.isApproved === 1
                    ).length;
                    const ongoingCount = employeeLogs.filter(
                      (log) => !log.endTime
                    ).length;

                    return (
                      <Paper
                        key={employeeKey}
                        variant="outlined"
                        sx={{ mb: 1, '&:last-child': { mb: 0 }, overflow: 'hidden' }}
                      >
                        <Box
                          onClick={() =>
                            setExpandedSubGroups((prev) => ({
                              ...prev,
                              __nonJobActivities:
                                prev.__nonJobActivities === employeeKey ? null : employeeKey,
                            }))
                          }
                          sx={{
                            px: 1.25,
                            py: 0.75,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            bgcolor: employeeExpanded
                              ? alpha(theme.palette.grey[500], 0.07)
                              : 'background.paper',
                          }}
                        >
                          {employeeExpanded ? (
                            <KeyboardArrowDown fontSize="small" />
                          ) : (
                            <KeyboardArrowRight fontSize="small" />
                          )}
                          <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                            {employeeKey}
                          </Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${approvedCount}/${employeeLogs.length} approved`}
                          />
                          {ongoingCount > 0 && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${ongoingCount} ongoing`}
                            />
                          )}
                          {pendingCompletedLogs.length > 0 && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleApprove(pendingCompletedLogs);
                              }}
                              sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                              Approve Pending ({pendingCompletedLogs.length})
                            </Button>
                          )}
                        </Box>

                        <Collapse in={employeeExpanded} unmountOnExit>
                          <Box sx={{ p: 1 }}>
                            <DataGrid
                              rows={buildTimelineRows(employeeLogs)}
                              columns={nonJobDetailColumns}
                              density="compact"
                              getRowClassName={(params) =>
                                getActivityRowClass(params.row, highlightedRows)
                              }
                              disableColumnMenu
                              autoHeight
                              hideFooter={employeeLogs.length <= 10}
                              sx={gridCommonSX}
                            />
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}
                </Box>
              </Paper>
            )}

            {allJobEntries.length === 0 && nonJobLogs.length === 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  textAlign: 'center',
                  borderStyle: 'dashed',
                  color: 'text.secondary',
                }}
              >
                <Typography fontWeight={600}>No activity records in this time range</Typography>
                <Typography variant="body2">
                  Select another time range or adjust the search filter.
                </Typography>
              </Paper>
            )}

            {jobStatusFilter !== 'all' &&
              (allJobEntries.length > 0 || nonJobLogs.length > 0) &&
              filteredJobEntries.length === 0 &&
              filteredNonJobLogs.length === 0 && (
                <Paper
                  variant="outlined"
                  sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed', color: 'text.secondary' }}
                >
                  <Typography fontWeight={600}>
                    No {jobStatusFilter} jobs or non-job activities in this time range
                  </Typography>
                  <Button size="small" onClick={() => handleJobStatusFilter('all')} sx={{ mt: 1 }}>
                    Show all activities
                  </Button>
                </Paper>
              )}

            {filteredJobEntries.map(([jobNumber, jobLogs]) => {
              const jobExpanded = expandedGroup === jobNumber;
              const toggleJob = () =>
                setExpandedGroup((prev) => (prev === jobNumber ? null : jobNumber));

              // Group by Employee
              const employeeGroups = jobLogs.reduce((acc, log) => {
                const empKey = log.employee || "Unknown Employee";
                if (!acc[empKey]) acc[empKey] = [];
                acc[empKey].push(log);
                return acc;
              }, {});

              const totalProd = jobLogs.reduce((s, l) => s + (Number(l.prodQty) || 0), 0);
              const totalWaste = jobLogs.reduce((s, l) => s + (Number(l.wasteQty) || 0), 0);

              // ✅ Calculate job-level statuses (needed for summary panel and border colors)
              const hasOngoingOperations = jobLogs.some((l) => !l.endTime || l.endTime === '' || l.endTime === null);
              const allJobRecordsApproved = jobLogs.length > 0 && jobLogs.every((l) => l.isApproved === 1);
              const pendingApprovalCount = jobLogs.filter((l) => l.isApproved !== 1).length;
              const employeeCount = Object.keys(employeeGroups).length;

              // Determine job status color and border
              let jobStatusColor = theme.palette.primary.main;
              let jobStatusBgColor = alpha(theme.palette.primary.main, 0.2);

              if (hasOngoingOperations) {
                jobStatusColor = theme.palette.info.main;
                jobStatusBgColor = alpha(theme.palette.info.main, 0.25);
              } else if (allJobRecordsApproved) {
                jobStatusColor = theme.palette.success.main;
                jobStatusBgColor = alpha(theme.palette.success.main, 0.25);
              } else if (pendingApprovalCount > 0) {
                jobStatusColor = theme.palette.warning.main;
                jobStatusBgColor = alpha(theme.palette.warning.main, 0.25);
              }

              return (
                <Paper
                  key={jobNumber}
                  elevation={3}
                  sx={{
                    borderRadius: 1,
                    border: `3px solid ${jobStatusBgColor}`,
                    borderLeftWidth: '6px',
                    borderLeftColor: jobStatusColor,
                    bgcolor: alpha(theme.palette.background.paper, 0.98),
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: theme.shadows[8],
                      transform: "translateY(-2px)",
                      borderLeftWidth: '8px',
                    },
                  }}
                >
                  {/* ✅ JOB HEADER - Enhanced Colors */}
                  <Box
                    onClick={toggleJob}
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 2.5,
                      py: 1.75,
                      background: jobExpanded
                        ? `linear-gradient(135deg, ${alpha(jobStatusColor, 0.12)} 0%, ${alpha(jobStatusColor, 0.08)} 100%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.grey[200], 0.6)} 0%, ${alpha(theme.palette.grey[100], 0.4)} 100%)`,
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: `linear-gradient(135deg, ${alpha(jobStatusColor, 0.18)} 0%, ${alpha(jobStatusColor, 0.12)} 100%)`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        bgcolor: jobExpanded
                          ? alpha(jobStatusColor, 0.15)
                          : alpha(theme.palette.grey[400], 0.15),
                        color: jobExpanded ? jobStatusColor : theme.palette.text.secondary,
                        transition: "all 0.3s ease",
                      }}
                    >
                      {jobExpanded ? (
                        <KeyboardArrowDown fontSize="medium" />
                      ) : (
                        <KeyboardArrowRight fontSize="medium" />
                      )}
                    </Box>

                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{
                        color: theme.palette.text.primary,
                        letterSpacing: '0.5px',
                      }}
                    >
                      Job: {jobNumber}
                    </Typography>

                    {/* ✅ NEW: Condensed One-Line Summary */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: alpha(jobStatusColor, 0.2),
                        border: `1px solid ${alpha(jobStatusColor, 0.9)}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                        }}
                      >
                        {(() => {
                          // Calculate key metrics for one-line summary
                          const jobOngoingCount = jobLogs.filter(
                            l => !l.endTime || l.endTime === '' || l.endTime === null
                          ).length;
                          const jobPendingCount = jobLogs.filter(l => l.isApproved !== 1).length;
                          const jobApprovedCount = jobLogs.filter(l => l.isApproved === 1).length;
                          const jobApprovalRate = jobLogs.length > 0
                            ? ((jobApprovedCount / jobLogs.length) * 100).toFixed(0)
                            : 0;
                          const uniqueJobStarts = new Set(
                            jobLogs
                              .map(l => l.jobStartedTimeRaw)
                              .filter(raw => raw && raw !== null && raw !== '')
                          ).size;
                          const jobWasteRate = totalProd > 0
                            ? ((totalWaste / (totalProd + totalWaste)) * 100).toFixed(1)
                            : 0;

                          // Build summary string
                          const parts = [];

                          if (jobOngoingCount > 0) {
                            parts.push(`🔄 ${jobOngoingCount} Ongoing`);
                          }

                          if (jobPendingCount > 0) {
                            parts.push(`⏱ ${jobPendingCount} Pending`);
                          }

                          parts.push(`✓ ${jobApprovalRate}% Approved`);
                          parts.push(`👥 ${employeeCount} Emp`);
                          parts.push(`🚀 ${uniqueJobStarts} Start${uniqueJobStarts !== 1 ? 's' : ''}`);

                          if (parseFloat(jobWasteRate) > 10) {
                            parts.push(`⚠ ${jobWasteRate}% Waste`);
                          } else {
                            parts.push(`${jobWasteRate}% Waste`);
                          }

                          return parts.join('  •  ');
                        })()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", gap: 0.75, ml: "auto", alignItems: "center" }}>
                      <Chip
                        label={`${jobLogs.length} Records`}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          color: theme.palette.info.dark,
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                        }}
                      />
                      <Chip
                        icon={<Box component="span" sx={{ fontSize: '1rem', ml: 1 }}>✓</Box>}
                        label={`${totalProd}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.12),
                          color: theme.palette.success.dark,
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          border: `1.5px solid ${alpha(theme.palette.success.main, 0.4)}`,
                          "& .MuiChip-icon": {
                            color: theme.palette.success.main,
                          }
                        }}
                      />
                      <Chip
                        icon={<Box component="span" sx={{ fontSize: '1rem', ml: 1 }}>⚠</Box>}
                        label={`${totalWaste}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.error.main, 0.12),
                          color: theme.palette.error.dark,
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          border: `1.5px solid ${alpha(theme.palette.error.main, 0.4)}`,
                          "& .MuiChip-icon": {
                            color: theme.palette.error.main,
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  {/* JOB BODY */}
                  {jobExpanded && (
                    <Box sx={{ p: 1.5 }}>
                      {/* ✅ Job-Specific Summary Panel - Minimized */}

                      {/* <Paper
                        elevation={1}
                        sx={{
                          mb: 2,
                          p: 1.5,
                          borderRadius: 1,
                          background: `linear-gradient(135deg, ${alpha(jobStatusColor, 0.03)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
                          border: `1px solid ${alpha(jobStatusColor, 0.15)}`,
                          position: 'relative',
                        }}
                      >
                       
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '4px',
                              bgcolor: alpha(jobStatusColor, 0.12),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography fontSize="0.9rem">📋</Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2 }}>
                              Job Summary: {jobNumber}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              Status and metrics breakdown
                            </Typography>
                          </Box>
                        </Box>

                        {(() => {
                          // Calculate job-specific metrics
                          const jobTotalRecords = jobLogs.length;
                          const jobEmployeeCount = Object.keys(employeeGroups).length;

                          // Get unique job start times for this job
                          const uniqueJobStarts = new Set(
                            jobLogs
                              .map(l => l.jobStartedTimeRaw)
                              .filter(raw => raw && raw !== null && raw !== '')
                          );
                          const jobStartCount = uniqueJobStarts.size;

                          // Count by approval status
                          const jobPendingRecords = jobLogs.filter(l => l.isApproved !== 1).length;
                          const jobApprovedRecords = jobLogs.filter(l => l.isApproved === 1).length;
                          const jobApprovalRate = jobTotalRecords > 0
                            ? ((jobApprovedRecords / jobTotalRecords) * 100).toFixed(1)
                            : 0;

                          // Count ongoing operations
                          const jobOngoingCount = jobLogs.filter(
                            l => !l.endTime || l.endTime === '' || l.endTime === null
                          ).length;

                          // Production metrics
                          const jobWasteRate = totalProd > 0
                            ? ((totalWaste / (totalProd + totalWaste)) * 100).toFixed(1)
                            : 0;

                          // Get customer and product info
                        
                          return (
                            <>
                              


                              
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 0.75,
                                    borderRadius: 1,
                                    bgcolor: alpha(theme.palette.grey[50], 0.5),
                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography fontSize="0.75rem">
                                        {hasOngoingOperations ? '🔄' : allJobRecordsApproved ? '✓' : '⏱'}
                                      </Typography>
                                      <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.65rem' }} color="text.secondary">
                                        Status:
                                      </Typography>
                                      <Chip
                                        label={hasOngoingOperations ? 'ONGOING' : allJobRecordsApproved ? 'COMPLETED' : 'PENDING'}
                                        size="small"
                                        sx={{
                                          bgcolor: hasOngoingOperations
                                            ? alpha(theme.palette.info.main, 0.2)
                                            : allJobRecordsApproved
                                              ? alpha(theme.palette.success.main, 0.2)
                                              : alpha(theme.palette.warning.main, 0.2),
                                          color: hasOngoingOperations
                                            ? theme.palette.info.dark
                                            : allJobRecordsApproved
                                              ? theme.palette.success.dark
                                              : theme.palette.warning.dark,
                                          fontWeight: 700,
                                          fontSize: '0.65rem',
                                          height: 18,
                                        }}
                                      />
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                                        Total Records:
                                      </Typography>
                                      <Chip label={jobTotalRecords} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.dark, fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="caption" color="info.dark" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                                        Ongoing Ops:
                                      </Typography>
                                      <Chip label={jobOngoingCount} size="small" sx={{ bgcolor: alpha(theme.palette.info.main, 0.2), color: theme.palette.info.dark, fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="caption" color="warning.dark" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                                        Pending:
                                      </Typography>
                                      <Chip label={jobPendingRecords} size="small" sx={{ bgcolor: alpha(theme.palette.warning.main, 0.2), color: theme.palette.warning.dark, fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="caption" color="success.dark" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                                        Approved:
                                      </Typography>
                                      <Chip label={jobApprovedRecords} size="small" sx={{ bgcolor: alpha(theme.palette.success.main, 0.2), color: theme.palette.success.dark, fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
                                    </Box>
                                  </Box>
                                </Paper>

                                
                                <Paper
                                  elevation={0}
                                  sx={{
                                    p: 0.75,
                                    borderRadius: 1,
                                    bgcolor: alpha(theme.palette.success.main, 0.03),
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="caption" color="info.dark" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                                        Approval Rate:
                                      </Typography>
                                      <Chip label={`${jobApprovalRate}%`} size="small" sx={{ bgcolor: alpha(theme.palette.info.main, 0.2), color: theme.palette.info.dark, fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <GroupIcon sx={{ fontSize: '0.75rem', color: theme.palette.secondary.main }} />
                                      <Typography variant="caption" color="secondary.dark" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                                        Employees:
                                      </Typography>
                                      <Chip label={jobEmployeeCount} size="small" sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.2), color: theme.palette.secondary.dark, fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="caption" color="primary.dark" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                                        Job Sessions:
                                      </Typography>
                                      <Chip label={jobStartCount} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.dark, fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
                                    </Box>
                                    <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography variant="caption" color="error.dark" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                                        ⚠ Waste Rate:
                                      </Typography>
                                      <Chip label={`${jobWasteRate}%`} size="small" sx={{ bgcolor: alpha(theme.palette.error.main, 0.25), color: theme.palette.error.dark, fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
                                    </Box>
                                  </Box>
                                </Paper>
                              </Box>
                            </>
                          );
                        })()}
                      </Paper> */}

                      {/* Employee Groups Below */}
                      {Object.entries(employeeGroups).map(([empKey, empLogs]) => {
                        const empExpanded = expandedSubGroups[jobNumber] === empKey;

                        const empProd = empLogs.reduce(
                          (sum, l) => sum + (Number(l.prodQty) || 0),
                          0
                        );
                        const empWaste = empLogs.reduce(
                          (sum, l) => sum + (Number(l.wasteQty) || 0),
                          0
                        );
                        const empMetrics = calculateEmployeeTimeMetrics(empLogs);

                        // Sub-groups with Job Started DateTime
                        const subGroups = Object.entries(
                          empLogs.reduce((acc, log) => {
                            const jobStartedRaw = log.jobStartedTimeRaw || 'no-start-time';
                            const groupingKey = `${log.jobNumber}|${log.customer}|${log.product}|${log.mainOperation}|${jobStartedRaw}`;

                            if (!acc[groupingKey]) {
                              acc[groupingKey] = {
                                logs: [],
                                metadata: {
                                  jobNumber: log.jobNumber,
                                  customer: log.customer,
                                  product: log.product,
                                  mainOperation: log.mainOperation,
                                  jobStartedTime: log.jobStartedTime,
                                  jobStartedTimeRaw: log.jobStartedTimeRaw,
                                  upCount: log.upCount,
                                }
                              };
                            }
                            if (log.upCount !== null && log.upCount !== undefined) {
                              acc[groupingKey].metadata.upCount = log.upCount;
                            }
                            acc[groupingKey].logs.push(log);
                            return acc;
                          }, {})
                        );

                        // Employee-level approval chip
                        const allEmpApprovables = empLogs.filter(
                          (r) =>
                            !r.jobStartedTimeRaw ||
                            (r.jobStartedTimeRaw &&
                              r.operationDescription === "JOB_STARTED")
                        );
                        const allApproved =
                          allEmpApprovables.length > 0 &&
                          allEmpApprovables.every((r) => r.isApproved === 1);
                        const someApproved = allEmpApprovables.some(
                          (r) => r.isApproved === 1
                        );

                        return (
                          <Box key={empKey} sx={{ mb: 2.5 }}>
                            {/* ✅ EMPLOYEE HEADER - Enhanced Colors */}
                            <Box
                              onClick={() =>
                                setExpandedSubGroups((prev) => ({
                                  ...prev,
                                  [jobNumber]:
                                    prev[jobNumber] === empKey ? null : empKey,
                                }))
                              }
                              sx={{
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                background: empExpanded
                                  ? `linear-gradient(90deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`
                                  : alpha(theme.palette.grey[100], 0.5),
                                p: 1.5,
                                borderRadius: 1.5,
                                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                mb: empExpanded ? 1.5 : 0,
                                transition: "all 0.25s ease",
                                "&:hover": {
                                  background: `linear-gradient(90deg, ${alpha(theme.palette.secondary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.light, 0.08)} 100%)`,
                                  boxShadow: `0 2px 8px ${alpha(theme.palette.grey[400], 0.2)}`,
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 28,
                                  height: 28,
                                  borderRadius: "6px",
                                  bgcolor: empExpanded
                                    ? alpha(theme.palette.secondary.main, 0.15)
                                    : alpha(theme.palette.grey[400], 0.12),
                                  color: empExpanded ? theme.palette.secondary.main : theme.palette.text.secondary,
                                }}
                              >
                                {empExpanded ? (
                                  <KeyboardArrowDown fontSize="small" />
                                ) : (
                                  <KeyboardArrowRight fontSize="small" />
                                )}
                              </Box>

                              <GroupIcon
                                fontSize="small"
                                sx={{
                                  color: theme.palette.secondary.main,
                                  opacity: 0.7,
                                }}
                              />

                              <Typography
                                variant="subtitle1"
                                fontWeight={700}
                                sx={{ color: theme.palette.text.primary }}
                              >
                                {empKey}
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.75,
                                  ml: "auto",
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  flexWrap: "wrap",
                                }}
                              >
                                <Chip
                                  label={`${empLogs.length} Records`}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(theme.palette.info.main, 0.08),
                                    color: theme.palette.info.dark,
                                    fontWeight: 600,
                                    fontSize: '0.7rem',
                                    height: 22,
                                  }}
                                />
                                <Chip
                                  label={`${empMetrics.sessions} Sessions`}
                                  size="small"
                                  sx={{ fontWeight: 700, fontSize: '0.68rem', height: 24 }}
                                />
                                <Chip
                                  label={`Total ${empMetrics.total}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontWeight: 700, fontSize: '0.68rem', height: 24 }}
                                />
                                <Chip
                                  label={`Prod Time ${empMetrics.production}`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{ fontWeight: 700, fontSize: '0.68rem', height: 24 }}
                                />
                                <Chip
                                  label={`Idle/Setup ${empMetrics.idle}`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ fontWeight: 700, fontSize: '0.68rem', height: 24 }}
                                />
                                <Chip
                                  label={`Meal ${empMetrics.meal}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 700, fontSize: '0.68rem', height: 24 }}
                                />
                                <Chip
                                  label={`Non-commanded ${empMetrics.nonCommanded}`}
                                  size="small"
                                  color={Number(empMetrics.commandedRate) >= 90 ? "success" : "error"}
                                  sx={{ fontWeight: 700, fontSize: '0.68rem', height: 24 }}
                                />
                                <Chip
                                  label={`Commanded ${empMetrics.commandedRate}%`}
                                  size="small"
                                  color={Number(empMetrics.commandedRate) >= 90 ? "success" : "warning"}
                                  variant="outlined"
                                  sx={{ fontWeight: 700, fontSize: '0.68rem', height: 24 }}
                                />

                                <Chip
                                  label={`✓ ${empProd}`}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(theme.palette.success.main, 0.1),
                                    color: theme.palette.success.dark,
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    height: 24,
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                                  }}
                                />
                                <Chip
                                  label={`⚠ ${empWaste}`}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(theme.palette.error.main, 0.1),
                                    color: theme.palette.error.dark,
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    height: 24,
                                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                  }}
                                />
                                {allApproved ? (
                                  <Chip
                                    label="✓ Approved"
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(theme.palette.success.main, 0.15),
                                      color: theme.palette.success.dark,
                                      fontWeight: 700,
                                      fontSize: '0.7rem',
                                      height: 24,
                                      border: `1.5px solid ${theme.palette.success.main}`,
                                      ml: 0.5,
                                    }}
                                  />
                                ) : someApproved ? (
                                  <Chip
                                    label="⏳ Partial"
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                                      color: theme.palette.warning.dark,
                                      fontWeight: 700,
                                      fontSize: '0.7rem',
                                      height: 24,
                                      border: `1.5px solid ${theme.palette.warning.main}`,
                                      ml: 0.5,
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    label="⏱ Pending"
                                    size="small"
                                    sx={{
                                      bgcolor: alpha(theme.palette.grey[400], 0.15),
                                      color: theme.palette.text.secondary,
                                      fontWeight: 600,
                                      fontSize: '0.7rem',
                                      height: 24,
                                      border: `1px solid ${alpha(theme.palette.grey[400], 0.4)}`,
                                      ml: 0.5,
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>

                            {/* ✅ SUB-GROUPS - Enhanced Colors */}
                            {empExpanded &&
                              subGroups.map(([subKey, subData]) => {
                                const subLogs = subData.logs;
                                const meta = subData.metadata;


                                // ✅ UPDATED: Check ALL logs, not just approvable ones
                                const allApproved = subLogs.length > 0 && subLogs.every((l) => l.isApproved === 1);
                                const someApproved = subLogs.some((l) => l.isApproved === 1);
                                const canApprove = subLogs.some((l) => l.isApproved !== 1);

                                // ✅ NEW: Check if any job is still ongoing (no endTime)
                                const hasOngoingJob = subLogs.some((l) => !l.endTime || l.endTime === '' || l.endTime === null);

                                const subProd = subLogs.reduce(
                                  (sum, l) => sum + (Number(l.prodQty) || 0),
                                  0
                                );
                                const subWaste = subLogs.reduce(
                                  (sum, l) => sum + (Number(l.wasteQty) || 0),
                                  0
                                );

                                const subExpanded = expandedSubDetails[subKey] || false;
                                const toggleSubExpand = () =>
                                  setExpandedSubDetails((prev) => ({
                                    ...prev,
                                    [subKey]: !prev[subKey],
                                  }));

                                return (
                                  <Box key={subKey} sx={{ mb: 2, ml: 2 }}>
                                    {/* ✅ SUB-GROUP HEADER - Enhanced Colors */}
                                    <Box
                                      onClick={toggleSubExpand}
                                      sx={{
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        p: 1.5,
                                        pl: 2.5,
                                        background: subExpanded
                                          ? `linear-gradient(90deg, ${alpha(theme.palette.info.main, 0.06)} 0%, ${alpha(theme.palette.info.light, 0.03)} 100%)`
                                          : alpha(theme.palette.grey[50], 0.8),
                                        borderRadius: 1.5,
                                        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                                        borderLeft: `4px solid ${subExpanded ? theme.palette.info.main : alpha(theme.palette.grey[400], 0.4)}`,
                                        mb: 1,
                                        transition: "all 0.25s ease",
                                        "&:hover": {
                                          background: `linear-gradient(90deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
                                          borderLeft: `4px solid ${theme.palette.info.main}`,
                                          boxShadow: `0 2px 6px ${alpha(theme.palette.grey[400], 0.15)}`,
                                        },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          width: 24,
                                          height: 24,
                                          borderRadius: "4px",
                                          bgcolor: subExpanded
                                            ? alpha(theme.palette.info.main, 0.12)
                                            : alpha(theme.palette.grey[400], 0.1),
                                          color: subExpanded ? theme.palette.info.main : theme.palette.text.secondary,
                                        }}
                                      >
                                        {subExpanded ? (
                                          <KeyboardArrowDown fontSize="small" />
                                        ) : (
                                          <KeyboardArrowRight fontSize="small" />
                                        )}
                                      </Box>

                                      <Box sx={{ flex: 1 }}>
                                        <Typography
                                          variant="body2"
                                          fontWeight={700}
                                          sx={{
                                            color: theme.palette.text.primary,
                                            lineHeight: 1.4,
                                          }}
                                        >
                                          {meta.jobNumber} • {meta.customer} • {meta.product} • {meta.mainOperation}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            mt: 0.5,
                                          }}
                                        >
                                          <ScheduleIcon
                                            fontSize="inherit"
                                            sx={{
                                              fontSize: '0.9rem',
                                              color: theme.palette.info.main,
                                            }}
                                          />
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: theme.palette.info.dark,
                                              fontWeight: 600,
                                              fontSize: '0.7rem',
                                            }}
                                          >
                                            Started: {meta.jobStartedTime || 'N/A'}
                                          </Typography>
                                        </Box>
                                      </Box>

                                      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                                        {meta.upCount && (
                                          <Chip
                                            label={`Ups ${meta.upCount}`}
                                            size="small"
                                            sx={{
                                              bgcolor: alpha(theme.palette.warning.main, 0.12),
                                              color: theme.palette.warning.dark,
                                              fontWeight: 700,
                                              height: 26,
                                              border: `1.5px solid ${alpha(theme.palette.warning.main, 0.35)}`,
                                            }}
                                          />
                                        )}
                                        <Chip
                                          label={`✓ ${subProd}`}
                                          size="small"
                                          sx={{
                                            bgcolor: alpha(theme.palette.success.main, 0.12),
                                            color: theme.palette.success.dark,
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            height: 26,
                                            border: `1.5px solid ${alpha(theme.palette.success.main, 0.35)}`,
                                            boxShadow: `0 1px 3px ${alpha(theme.palette.success.main, 0.1)}`,
                                          }}
                                        />
                                        <Chip
                                          label={`⚠ ${subWaste}`}
                                          size="small"
                                          sx={{
                                            bgcolor: alpha(theme.palette.error.main, 0.12),
                                            color: theme.palette.error.dark,
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            height: 26,
                                            border: `1.5px solid ${alpha(theme.palette.error.main, 0.35)}`,
                                            boxShadow: `0 1px 3px ${alpha(theme.palette.error.main, 0.1)}`,
                                          }}
                                        />




                                        {allApproved ? (
                                          <Chip
                                            icon={<Box component="span" sx={{ fontSize: '0.9rem', ml: 0.5 }}>✓</Box>}
                                            label="All Approved"
                                            size="small"
                                            sx={{
                                              bgcolor: alpha(theme.palette.success.main, 0.18),
                                              color: theme.palette.success.dark,
                                              fontWeight: 700,
                                              fontSize: '0.7rem',
                                              height: 26,
                                              border: `2px solid ${theme.palette.success.main}`,
                                              boxShadow: `0 2px 4px ${alpha(theme.palette.success.main, 0.2)}`,
                                              ml: 1,
                                              "& .MuiChip-icon": {
                                                color: theme.palette.success.dark,
                                              }
                                            }}
                                          />
                                        ) : hasOngoingJob ? (
                                          <Chip
                                            icon={<Box component="span" sx={{ fontSize: '0.9rem', ml: 0.5 }}>🔄</Box>}
                                            label="Ongoing Job"
                                            size="small"
                                            sx={{
                                              bgcolor: alpha(theme.palette.info.main, 0.18),
                                              color: theme.palette.info.dark,
                                              fontWeight: 700,
                                              fontSize: '0.7rem',
                                              height: 26,
                                              border: `2px solid ${theme.palette.info.main}`,
                                              boxShadow: `0 2px 4px ${alpha(theme.palette.info.main, 0.2)}`,
                                              ml: 1,
                                              "& .MuiChip-icon": {
                                                color: theme.palette.info.dark,
                                              }
                                            }}
                                          />
                                        ) : someApproved ? (
                                          <>
                                            <Chip
                                              icon={<Box component="span" sx={{ fontSize: '0.9rem', ml: 0.5 }}>⏳</Box>}
                                              label="Partial"
                                              size="small"
                                              sx={{
                                                bgcolor: alpha(theme.palette.warning.main, 0.18),
                                                color: theme.palette.warning.dark,
                                                fontWeight: 700,
                                                fontSize: '0.7rem',
                                                height: 26,
                                                border: `2px solid ${theme.palette.warning.main}`,
                                                boxShadow: `0 2px 4px ${alpha(theme.palette.warning.main, 0.2)}`,
                                                ml: 1,
                                                "& .MuiChip-icon": {
                                                  color: theme.palette.warning.dark,
                                                }
                                              }}
                                            />
                                            <Button
                                              variant="contained"
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleApprove(subLogs); // ✅ Pass ALL logs
                                              }}
                                              sx={{
                                                ml: 1,
                                                textTransform: "none",
                                                fontWeight: 700,
                                                fontSize: '0.75rem',
                                                height: 28,
                                                background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                                                boxShadow: `0 2px 6px ${alpha(theme.palette.success.main, 0.4)}`,
                                                "&:hover": {
                                                  background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
                                                  boxShadow: `0 4px 10px ${alpha(theme.palette.success.main, 0.5)}`,
                                                }
                                              }}
                                            >
                                              ✓ Approve Remaining
                                            </Button>
                                          </>
                                        ) : canApprove ? (
                                          <Button
                                            variant="contained"
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleApprove(subLogs); // ✅ Pass ALL logs
                                            }}
                                            sx={{
                                              ml: 1,
                                              textTransform: "none",
                                              fontWeight: 700,
                                              fontSize: '0.75rem',
                                              height: 28,
                                              background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                                              boxShadow: `0 2px 6px ${alpha(theme.palette.success.main, 0.4)}`,
                                              "&:hover": {
                                                background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
                                                boxShadow: `0 4px 10px ${alpha(theme.palette.success.main, 0.5)}`,
                                              }
                                            }}
                                          >
                                            ✓ Approve All
                                          </Button>
                                        ) : null}
                                      </Box>
                                    </Box>

                                    {/* COLLAPSIBLE DATAGRID */}
                                    <Collapse in={subExpanded} timeout="auto" unmountOnExit>
                                      <Box sx={{ pl: 1, pt: 0.5 }}>
                                        <DataGrid
                                          rows={subLogs}
                                          columns={detailColumns}
                                          pageSize={10}
                                          rowsPerPageOptions={[10, 25, 50]}
                                          pagination
                                          density="compact"
                                          disableColumnMenu
                                          autoHeight
                                          getRowClassName={(params) =>
                                            highlightedRows.includes(params.id)
                                              ? "row-highlight"
                                              : ""
                                          }
                                          sx={{
                                            "& .MuiDataGrid-cell": {
                                              fontSize: "13px",
                                              py: 0.5,
                                            },
                                            "& .MuiDataGrid-columnHeader": {
                                              py: 0.5,
                                              bgcolor: alpha(theme.palette.primary.main, 0.05),
                                              fontWeight: 700,
                                            },
                                            bgcolor: theme.palette.background.paper,
                                            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            "& .row-highlight": {
                                              background: `linear-gradient(90deg, ${alpha(theme.palette.success.light, 0.3)} 0%, ${alpha(theme.palette.success.light, 0.1)} 100%)`,
                                              transition: "background 0.5s ease",
                                              animation: "pulse 2s ease-in-out",
                                            },
                                            "@keyframes pulse": {
                                              "0%, 100%": {
                                                backgroundColor: alpha(theme.palette.success.light, 0.3),
                                              },
                                              "50%": {
                                                backgroundColor: alpha(theme.palette.success.light, 0.5),
                                              },
                                            },
                                          }}
                                        />
                                      </Box>
                                    </Collapse>
                                  </Box>
                                );
                              })}
                          </Box>
                        );
                      })}

                      {/* ✅ JOB FOOTER - Enhanced Colors */}
                      <Divider sx={{ my: 2, borderColor: alpha(theme.palette.divider, 0.2) }} />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 4,
                          pr: 2,
                          pt: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: theme.palette.success.main,
                              boxShadow: `0 0 8px ${alpha(theme.palette.success.main, 0.5)}`,
                            }}
                          />
                          <Typography
                            variant="body1"
                            fontWeight={700}
                            sx={{
                              color: theme.palette.success.dark,
                              fontSize: '0.95rem',
                            }}
                          >
                            Total Production: {totalProd}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: theme.palette.error.main,
                              boxShadow: `0 0 8px ${alpha(theme.palette.error.main, 0.5)}`,
                            }}
                          />
                          <Typography
                            variant="body1"
                            fontWeight={700}
                            sx={{
                              color: theme.palette.error.dark,
                              fontSize: '0.95rem',
                            }}
                          >
                            Total Waste: {totalWaste}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>
        );
      }



      default:
        return null;
    }
  }, [
    activeTab,
    detailActiveTab,
    employeeAnalysisTab,
    employeeViewMode,
    loading,
    manDetailLogs,
    manShiftLogs,
    searchTerm,
    jobStatusFilter,
    detailColumns,
    nonJobDetailColumns,
    shiftColumns,
    theme,
    highlightedRows,
    buildRowsWithConditionalGrouping,
    groupColumn,
    expandedGroup, expandedSubGroups, handleApprove, expandedSubDetails, selectedTimeRange, timeRanges
  ]);


  const jobSummaryCount = useMemo(() => {
    const jobNumbers = new Set(
      manDetailLogs
        .map((log) => String(log.jobNumber || '').trim())
        .filter(Boolean)
    );
    const hasNonJobActivities = manDetailLogs.some(
      (log) => String(log.jobNumber || '').trim() === ''
    );
    return jobNumbers.size + (hasNonJobActivities ? 1 : 0);
  }, [manDetailLogs]);

  const tabs = useMemo(
    () => [
      { label: 'Shift Logs', icon: <ScheduleIcon fontSize="small" />, count: manShiftLogs.length },
      { label: 'Raw Logs', icon: <GroupIcon fontSize="small" />, count: manDetailLogs.length },
      { label: 'Employee Analysis', icon: <GroupIcon fontSize="small" />, count: new Set(manDetailLogs.map((log) => log.empId).filter(Boolean)).size },
      { label: 'Job Analysis', icon: <AssignmentIcon fontSize="small" />, count: jobSummaryCount },
    ],
    [jobSummaryCount, manDetailLogs, manShiftLogs.length]
  );

  // ------------------------
  // Render Component
  // ------------------------
  return (
    <>
      <Tooltip title="View employee logs for this station">
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={20} /> : <GridViewIcon />}
          onClick={() => setDialogOpen(true)}
          disabled={loading || disabled}
          sx={sx}
        >
          {loading ? 'Loading...' : 'Operations'}
        </Button>
      </Tooltip>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth={false}
        scroll="paper"
        keepMounted
        PaperProps={{
          sx: {

            width: { md: '95vw' },
            height: { md: '95vh' },
            // Ensure maxHeight is also set for consistency.
            maxHeight: { md: '95vh' },
          },
        }}
      >
        <DialogTitle sx={{ px: 1.5, py: 0.75 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: 32,
            }}
          >
            <Typography variant="subtitle2">Employee Logs - {machineCode}</Typography>
            <IconButton onClick={() => setDialogOpen(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Box
          sx={{
            px: 1.5,
            pt: 0.75,
            pb: 0,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 1,
              mb: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
              <Typography variant="caption" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                Time Range:
              </Typography>
              <Box
                sx={{
                  minWidth: 0,
                  overflowX: 'auto',
                  pb: 0.25,
                  '&::-webkit-scrollbar': { height: 4 },
                }}
              >
                <ButtonGroup variant="outlined" size="small" sx={{ whiteSpace: 'nowrap' }}>
                  {Object.entries(timeRanges).map(([k, r]) => (
                    <Button
                      key={k}
                      onClick={() => setSelectedTimeRange(k)}
                      startIcon={r.icon}
                      variant={selectedTimeRange === k ? 'contained' : 'outlined'}
                      sx={{ px: 1, minWidth: 'auto', whiteSpace: 'nowrap' }}
                    >
                      {r.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>
            </Box>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', md: 260 },
                flexShrink: 0,
                '& .MuiInputBase-root': { height: 36 },
              }}
            />
          </Box>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 42,
              '& .MuiTab-root': {
                minHeight: 42,
                py: 0.5,
                px: 1.5,
                textTransform: 'none',
              },
            }}
          >
            {tabs.map((tab, i) => (
              <Tab
                key={i}
                icon={tab.icon}
                iconPosition="start"
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {tab.label}
                    {tab.count > 0 && (
                      <Chip
                        label={tab.count}
                        size="small"
                        sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
                      />
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>
        <DialogContent
          dividers
          sx={{
            p: 0,
            flex: '1 1 auto',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
            {renderTabContent()}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Updated Edit Job Details Dialog ---------------- */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            >
              <AssignmentIcon />
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Edit Job Details
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Modify job information and status details
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Divider sx={{ mt: 2 }} />

        <DialogContent sx={{ pt: 3, px: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* ========== BASIC DETAILS SECTION ========== */}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color="primary.main"
                sx={{
                  mb: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem',
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 16,
                    bgcolor: theme.palette.primary.main,
                    borderRadius: 1,
                  }}
                />
                Basic Information
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Job Number"
                  value={formValues.job_number}
                  onChange={(e) => setFormValues({ ...formValues, job_number: e.target.value })}
                  fullWidth
                  size="small"
                  sx={{
                    transition: 'all 0.3s ease',
                    '& .MuiOutlinedInput-root': {
                      bgcolor: isFieldChanged(editRow?.jobNumber, formValues.job_number)
                        ? alpha(theme.palette.warning.light, 0.15)
                        : 'transparent',
                      '&.Mui-focused': {
                        bgcolor: 'transparent',
                      },
                    },
                  }}
                />

                <TextField
                  label="Customer"
                  value={formValues.customer}
                  onChange={(e) => setFormValues({ ...formValues, customer: e.target.value })}
                  fullWidth
                  size="small"
                  sx={{
                    transition: 'all 0.3s ease',
                    '& .MuiOutlinedInput-root': {
                      bgcolor: isFieldChanged(editRow?.customer, formValues.customer)
                        ? alpha(theme.palette.warning.light, 0.15)
                        : 'transparent',
                      '&.Mui-focused': {
                        bgcolor: 'transparent',
                      },
                    },
                  }}
                />

                <TextField
                  label="Product"
                  value={formValues.product}
                  onChange={(e) => setFormValues({ ...formValues, product: e.target.value })}
                  fullWidth
                  size="small"
                  sx={{
                    transition: 'all 0.3s ease',
                    '& .MuiOutlinedInput-root': {
                      bgcolor: isFieldChanged(editRow?.product, formValues.product)
                        ? alpha(theme.palette.warning.light, 0.15)
                        : 'transparent',
                      '&.Mui-focused': {
                        bgcolor: 'transparent',
                      },
                    },
                  }}
                />

                <TextField
                  label="Main Operation"
                  value={formValues.main_operation}
                  onChange={(e) =>
                    setFormValues({ ...formValues, main_operation: e.target.value })
                  }
                  fullWidth
                  size="small"
                  sx={{
                    transition: 'all 0.3s ease',
                    '& .MuiOutlinedInput-root': {
                      bgcolor: isFieldChanged(editRow?.mainOperation, formValues.main_operation)
                        ? alpha(theme.palette.warning.light, 0.15)
                        : 'transparent',
                      '&.Mui-focused': {
                        bgcolor: 'transparent',
                      },
                    },
                  }}
                />
              </Box>
            </Box>

            {/* ========== JOB_STARTED ONLY SECTION ========== */}
            {editRow?.operationDescription === 'JOB_STARTED' && (
              <>
                {/* Quantities Section - Side by Side */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="success.main"
                    sx={{
                      mb: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem',
                    }}
                  >
                    <Box
                      sx={{
                        width: 4,
                        height: 16,
                        bgcolor: theme.palette.success.main,
                        borderRadius: 1,
                      }}
                    />
                    Production Quantities
                  </Typography>

                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: isBreakingGroup ? '1fr 1fr 1fr' : '1fr 1fr',
                    gap: 2
                  }}>
                    {isBreakingGroup && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          border: `2px solid ${editRow && !eqNum(formValues.up_count, editRow?.upCount)
                            ? alpha(theme.palette.warning.main, 0.5)
                            : alpha(theme.palette.warning.main, 0.2)}`,
                          bgcolor: editRow && !eqNum(formValues.up_count, editRow?.upCount)
                            ? alpha(theme.palette.warning.light, 0.08)
                            : alpha(theme.palette.warning.main, 0.03),
                        }}
                      >
                        <Typography variant="body2" fontWeight={700} color="warning.dark" sx={{ mb: 1 }}>
                          Number of Ups
                        </Typography>
                        <FormControl fullWidth size="small">
                          <InputLabel>Ups Count</InputLabel>
                          <Select
                            label="Ups Count"
                            value={formValues.up_count ?? ''}
                            onChange={(e) =>
                              setFormValues((prev) => ({ ...prev, up_count: e.target.value }))
                            }
                          >
                            {UPS_OPTIONS.map((value) => (
                              <MenuItem key={value} value={value}>{value}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Paper>
                    )}

                    {/* Production Quantity */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        border: `2px solid ${editRow && !eqNum(formValues.prod_qty, editRow?.prodQty)
                          ? alpha(theme.palette.warning.main, 0.5)
                          : alpha(theme.palette.success.main, 0.2)
                          }`,
                        bgcolor: editRow && !eqNum(formValues.prod_qty, editRow?.prodQty)
                          ? alpha(theme.palette.warning.light, 0.08)
                          : alpha(theme.palette.success.main, 0.03),
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.success.main, 0.12),
                            color: theme.palette.success.dark,
                          }}
                        >
                          <Typography fontSize="1.2rem" fontWeight={700}>✓</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={700} color="success.dark">
                          Production Qty
                        </Typography>
                      </Box>
                      <TextField
                        type="text"
                        value={formValues.prod_qty ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (/^\d*$/.test(v)) {
                            setFormValues((prev) => ({ ...prev, prod_qty: v }));
                          }
                        }}
                        fullWidth
                        size="small"
                        placeholder="Enter quantity"
                        InputProps={{
                          sx: {
                            bgcolor: 'background.paper',
                            fontWeight: 600,
                            fontSize: '1rem',
                          }
                        }}
                      />
                    </Paper>

                    {/* Waste Quantity */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        border: `2px solid ${editRow && !eqNum(formValues.waste_qty, editRow?.wasteQty)
                          ? alpha(theme.palette.warning.main, 0.5)
                          : alpha(theme.palette.error.main, 0.2)
                          }`,
                        bgcolor: editRow && !eqNum(formValues.waste_qty, editRow?.wasteQty)
                          ? alpha(theme.palette.warning.light, 0.08)
                          : alpha(theme.palette.error.main, 0.03),
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.error.main, 0.12),
                            color: theme.palette.error.dark,
                          }}
                        >
                          <Typography fontSize="1.2rem" fontWeight={700}>⚠</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={700} color="error.dark">
                          Waste Qty
                        </Typography>
                      </Box>
                      <TextField
                        type="text"
                        value={formValues.waste_qty ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (/^\d*$/.test(v)) {
                            setFormValues((prev) => ({ ...prev, waste_qty: v }));
                          }
                        }}
                        fullWidth
                        size="small"
                        placeholder="Enter quantity"
                        InputProps={{
                          sx: {
                            bgcolor: 'background.paper',
                            fontWeight: 600,
                            fontSize: '1rem',
                          }
                        }}
                      />
                    </Paper>
                  </Box>
                </Box>

                {/* Status Section - Side by Side */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="info.main"
                    sx={{
                      mb: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem',
                    }}
                  >
                    <Box
                      sx={{
                        width: 4,
                        height: 16,
                        bgcolor: theme.palette.info.main,
                        borderRadius: 1,
                      }}
                    />
                    Job Status
                  </Typography>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    {/* Finished Good Status */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        border: `2px solid ${isFieldChanged(editRow?.finishedGoodStatus, formValues.finished_good_status)
                          ? alpha(theme.palette.warning.main, 0.5)
                          : alpha(theme.palette.info.main, 0.2)
                          }`,
                        bgcolor: isFieldChanged(editRow?.finishedGoodStatus, formValues.finished_good_status)
                          ? alpha(theme.palette.warning.light, 0.08)
                          : alpha(theme.palette.info.main, 0.03),
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <FormLabel
                        component="legend"
                        sx={{
                          fontWeight: 700,
                          mb: 1.5,
                          color: 'info.dark',
                          fontSize: '0.85rem',
                        }}
                      >
                        Finished Good Status
                      </FormLabel>
                      <RadioGroup
                        value={formValues.finished_good_status || editRow?.finishedGoodStatus || ''}
                        onChange={(e) =>
                          setFormValues({
                            ...formValues,
                            finished_good_status: e.target.value,
                          })
                        }
                        sx={{ gap: 1 }}
                      >
                        <FormControlLabel
                          value="yes"
                          control={
                            <Radio
                              size="small"
                              sx={{
                                '&.Mui-checked': {
                                  color: theme.palette.success.main,
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>Yes</Typography>
                              <Chip
                                label="Complete"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  bgcolor: alpha(theme.palette.success.main, 0.1),
                                  color: 'success.dark',
                                }}
                              />
                            </Box>
                          }
                          sx={{
                            p: 1,
                            pl: 1.5,
                            borderRadius: 1.5,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            bgcolor: formValues.finished_good_status === 'yes'
                              ? alpha(theme.palette.success.main, 0.08)
                              : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.success.main, 0.05),
                            },
                            '& .MuiFormControlLabel-label': {
                              fontSize: '0.9rem',
                              width: '100%',
                            },
                          }}
                        />
                        <FormControlLabel
                          value="no"
                          control={
                            <Radio
                              size="small"
                              sx={{
                                '&.Mui-checked': {
                                  color: theme.palette.error.main,
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>No</Typography>
                              <Chip
                                label="Incomplete"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  bgcolor: alpha(theme.palette.error.main, 0.1),
                                  color: 'error.dark',
                                }}
                              />
                            </Box>
                          }
                          sx={{
                            p: 1,
                            pl: 1.5,
                            borderRadius: 1.5,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            bgcolor: formValues.finished_good_status === 'no'
                              ? alpha(theme.palette.error.main, 0.08)
                              : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.error.main, 0.05),
                            },
                            '& .MuiFormControlLabel-label': {
                              fontSize: '0.9rem',
                              width: '100%',
                            },
                          }}
                        />
                      </RadioGroup>
                    </Paper>

                    {/* Job Status */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        border: `2px solid ${isFieldChanged(editRow?.jobStatus, formValues.job_status)
                          ? alpha(theme.palette.warning.main, 0.5)
                          : alpha(theme.palette.info.main, 0.2)
                          }`,
                        bgcolor: isFieldChanged(editRow?.jobStatus, formValues.job_status)
                          ? alpha(theme.palette.warning.light, 0.08)
                          : alpha(theme.palette.info.main, 0.03),
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <FormLabel
                        component="legend"
                        sx={{
                          fontWeight: 700,
                          mb: 1.5,
                          color: 'info.dark',
                          fontSize: '0.85rem',
                        }}
                      >
                        Job Status
                      </FormLabel>
                      <RadioGroup
                        value={formValues.job_status || editRow?.jobStatus || ''}
                        onChange={(e) =>
                          setFormValues({ ...formValues, job_status: e.target.value })
                        }
                        sx={{ gap: 1 }}
                      >
                        <FormControlLabel
                          value="complete"
                          control={
                            <Radio
                              size="small"
                              sx={{
                                '&.Mui-checked': {
                                  color: theme.palette.success.main,
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>Complete</Typography>
                              <Chip
                                label="✓"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.7rem',
                                  bgcolor: alpha(theme.palette.success.main, 0.15),
                                  color: 'success.dark',
                                  fontWeight: 700,
                                }}
                              />
                            </Box>
                          }
                          sx={{
                            p: 1,
                            pl: 1.5,
                            borderRadius: 1.5,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            bgcolor: formValues.job_status === 'complete'
                              ? alpha(theme.palette.success.main, 0.08)
                              : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.success.main, 0.05),
                            },
                            '& .MuiFormControlLabel-label': {
                              fontSize: '0.9rem',
                              width: '100%',
                            },
                          }}
                        />
                        <FormControlLabel
                          value="incomplete"
                          control={
                            <Radio
                              size="small"
                              sx={{
                                '&.Mui-checked': {
                                  color: theme.palette.warning.main,
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>Incomplete</Typography>
                              <Chip
                                label="⏳"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.7rem',
                                  bgcolor: alpha(theme.palette.warning.main, 0.15),
                                  color: 'warning.dark',
                                  fontWeight: 700,
                                }}
                              />
                            </Box>
                          }
                          sx={{
                            p: 1,
                            pl: 1.5,
                            borderRadius: 1.5,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            bgcolor: formValues.job_status === 'incomplete'
                              ? alpha(theme.palette.warning.main, 0.08)
                              : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.warning.main, 0.05),
                            },
                            '& .MuiFormControlLabel-label': {
                              fontSize: '0.9rem',
                              width: '100%',
                            },
                          }}
                        />
                      </RadioGroup>
                    </Paper>
                  </Box>
                </Box>

                {/* Remarks Section */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="text.primary"
                    sx={{
                      mb: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '0.75rem',
                    }}
                  >
                    <Box
                      sx={{
                        width: 4,
                        height: 16,
                        bgcolor: theme.palette.text.primary,
                        borderRadius: 1,
                      }}
                    />
                    Additional Notes
                  </Typography>

                  <TextField
                    label="Remarks"
                    value={formValues.remarks ?? editRow?.remarks ?? ''}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        remarks: e.target.value.slice(0, 100),
                      })
                    }
                    multiline
                    rows={3}
                    fullWidth
                    size="small"
                    helperText={`${formValues.remarks?.length || 0}/100 characters`}
                    placeholder="Add supervisor remarks..."
                    sx={{
                      transition: 'all 0.3s ease',
                      '& .MuiOutlinedInput-root': {
                        bgcolor: isFieldChanged(editRow?.remarks, formValues.remarks)
                          ? alpha(theme.palette.warning.light, 0.15)
                          : alpha(theme.palette.grey[100], 0.3),
                        '&.Mui-focused': {
                          bgcolor: 'transparent',
                        },
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2.5, gap: 0.75, justifyContent: 'space-between' }}>
          <Button
            onClick={handleResetForm}
            variant="outlined"
            color="inherit"
            startIcon={<ArchiveRestoreIcon size={18} />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1,
              px: 2.5,
            }}
          >
            Reset
          </Button>

          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <Button
              onClick={() => setEditDialogOpen(false)}
              variant="outlined"
              color="inherit"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1,
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              disabled={!isFormChanged}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 1,
                px: 3,
                background: isFormChanged
                  ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                  : theme.palette.action.disabledBackground,
                boxShadow: isFormChanged
                  ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`
                  : 'none',
                '&:hover': {
                  background: isFormChanged
                    ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                    : theme.palette.action.disabledBackground,
                  boxShadow: isFormChanged
                    ? `0 6px 16px ${alpha(theme.palette.primary.main, 0.5)}`
                    : 'none',
                },
              }}
            >
              Save Changes
            </Button>
          </Box>
        </DialogActions>
      </Dialog>




      {/* ---------------- Confirm Update Dialog ---------------- */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          <Typography>
            This will update <b>all records</b> for this job
            (same Job Started Time, Employee, Supervisor, and Station).<br />
            Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmSave} color="primary" variant="contained">
            Yes, Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Approval Confirmation Dialog ---------------- */}
      <Dialog
        open={approveConfirmOpen}
        onClose={() => setApproveConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.main,
              }}
            >
              <Typography fontSize="1.5rem">⚠️</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Confirm Approval
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Divider sx={{ mt: 2 }} />

        <DialogContent sx={{ pt: 3, px: 3 }}>

          <Paper
            elevation={0}
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.info.main, 0.05),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              You are about to approve ALL records in this group:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={`${pendingApprovalLogs.length} Record${pendingApprovalLogs.length !== 1 ? 's' : ''}`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
                <Typography variant="body2" fontWeight={600}>
                  for this employee's job
                </Typography>
              </Box>
            </Box>
          </Paper>


        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2.5, gap: 0.75 }}>
          <Button
            onClick={() => {
              setApproveConfirmOpen(false);
              setPendingApprovalLogs([]);
            }}
            variant="outlined"
            color="inherit"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 1,
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmApprove}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1,
              px: 3,
              background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.4)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`,
                boxShadow: `0 6px 16px ${alpha(theme.palette.success.main, 0.5)}`,
              },
            }}
          >
            ✓ Approve Records
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

OperationsButton.propTypes = {
  machineCode: PropTypes.string,
  groupCode: PropTypes.string,
  supervisorId: PropTypes.string,
  onTriggerSnackbar: PropTypes.func,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
};

export default React.memo(OperationsButton);

